import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Building2, Phone, Mail, Calendar, MessageSquare, FileText, Repeat, Ticket, Target, StickyNote, FileSignature, ScrollText, AlertTriangle, Send, ArrowDownLeft, ArrowUpRight, Reply, Paperclip, X, KeyRound, Archive, ArchiveRestore, Video, UserCheck, CalendarClock, XCircle, Trash2, CheckCircle2, Users, Clock, ExternalLink, Pencil, PhoneMissed, Eye, UserX, PhoneIncoming, PhoneOutgoing, PhoneCall, PhoneOff, Voicemail, FileAudio, ChevronDown, ChevronUp, MessageCircle, Headset, User } from "lucide-react";
import { api, formatApiErrorDetail } from "../../api";
import { useAuth } from "../../auth";

const ARCHIVE_REASONS = [
  { value: "perdida", label: "Perdida" },
  { value: "duplicada", label: "Duplicada" },
  { value: "spam", label: "Spam" },
  { value: "sem_interesse", label: "Sem interesse" },
  { value: "contacto_invalido", label: "Contacto invalido" },
  { value: "outro", label: "Outro" },
];
import { Btn, Badge, dt, eur } from "../../components/common";
import { Modal, Field, TextArea, Selecter } from "./crmui";
import { LigarButton, WebphoneDialog } from "../../components/WebphoneDialog";
import ContactDrawer from "../../components/ContactDrawer";
import SmsComposer from "../../components/SmsComposer";
import WhatsappComposer from "../../components/WhatsappComposer";
import ProposalDrawer from "../../components/ProposalDrawer";
import { overlayCloseHandlers } from "../../lib/overlayClose";
import { useAutoRefresh } from "../../lib/autoRefresh";
import ActivityModal from "./ActivityModal";
import { RichText } from "../../components/RichText";
import { emailSafeHtml, appendSignaturePreview } from "../../lib/emailPreview";

// Departamentos padrao para contactos multi-role (financeiro, DPO, CISO, CEO, TI...)
const DEPARTAMENTOS = [
  "Geral", "CEO", "Financeiro", "DPO", "CISO", "TI", "Compras", "Legal", "Marketing", "Outro",
];

const STAGES = ["novo", "qualificado", "proposta", "negociacao", "ganho", "perdido"];
const STAGE_LABEL = {
  novo: "Novo", qualificado: "Qualificado", proposta: "Proposta",
  negociacao: "Negociacao", ganho: "Ganho", perdido: "Perdido",
};
const STAGE_OPTIONS = STAGES.map((s) => ({ value: s, label: STAGE_LABEL[s] }));
const TIPO_ICON = {
  "oportunidade": Target, "subscricao": Repeat, "ticket": Ticket, "fatura": FileText,
  "proposta": FileSignature, "contrato": ScrollText, "email": Mail,
  "atividade:chamada": Phone, "atividade:email": Mail, "atividade:reuniao": Calendar,
  "atividade:demo": Calendar, "atividade:formulario": FileText, "atividade:nota": StickyNote,
  "atividade:tarefa": StickyNote, "atividade:agendamento": Calendar, "atividade:chat": MessageSquare,
  "atividade:chat-ai": MessageSquare, "atividade:handoff": Users,
  "atividade:sms-inbound": Phone, "atividade:sms-outbound": Send, "atividade:sms": Phone,
  "utilizador": KeyRound,
};

// Chamadas sem sucesso ganham um icone grande e colorido para se destacarem na timeline
export const CALL_RESULT_META = {
  // resultados de entrada manual CRM
  nao_atendeu: { label: "Nao atendeu", icon: PhoneMissed, dot: "bg-red-600", ring: "ring-red-200", badge: "bg-red-100 text-red-700 border-red-300" },
  contactar_mais_tarde: { label: "Contactar mais tarde", icon: Clock, dot: "bg-amber-500", ring: "ring-amber-200", badge: "bg-amber-100 text-amber-700 border-amber-300" },
  pessoa_indisponivel: { label: "Pessoa responsavel indisponivel", icon: UserX, dot: "bg-orange-500", ring: "ring-orange-200", badge: "bg-orange-100 text-orange-700 border-orange-300" },
  // resultados do LetsCall One
  atendeu: { label: "Atendida", icon: PhoneCall, dot: "bg-emerald-600", ring: "ring-emerald-200", badge: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  ocupado: { label: "Linha ocupada", icon: PhoneOff, dot: "bg-amber-600", ring: "ring-amber-200", badge: "bg-amber-100 text-amber-700 border-amber-300" },
  caixa_voz: { label: "Caixa de voz", icon: Voicemail, dot: "bg-slate-500", ring: "ring-slate-200", badge: "bg-slate-100 text-slate-700 border-slate-300" },
};

const TIMELINE_FILTERS = [
  { key: null,                  label: "Todos" },
  { key: "atividade:chamada",   label: "Chamadas" },
  { key: "atividade:email",     label: "Emails" },
  { key: "atividade:nota",      label: "Notas" },
  { key: "fatura",              label: "Faturas" },
];

export default function AccountDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { can, user } = useAuth();
  const isSuper = can("*");
  const [archiveModal, setArchiveModal] = useState(null);
  const [previewProp, setPreviewProp] = useState(null);
  const [acc, setAcc] = useState(null);
  const [tab, setTab] = useState("resumo");
  const [timeline, setTimeline] = useState([]);
  const [timelineFiltro, setTimelineFiltro] = useState(null); // null = Todos
  const [expandedTranscriptions, setExpandedTranscriptions] = useState({}); // {activityId: texto|null|"loading"}
  const [lastInter, setLastInter] = useState(null);
  const [showContact, setShowContact] = useState(false);
  const [highlightContactId, setHighlightContactId] = useState(null);
  const [showDeal, setShowDeal] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [showSms, setShowSms] = useState(false);
  const [smsContactos, setSmsContactos] = useState(null);
  const [showPhonePicker, setShowPhonePicker] = useState(false);
  const [phonePickerTarget, setPhonePickerTarget] = useState("");
  const [myLetsCallExt, setMyLetsCallExt] = useState("");
  const [mySignature, setMySignature] = useState("");
  // Carregar extensao LetsCall + assinatura do utilizador logado uma vez (para o WebphoneDialog e preview de email)
  useEffect(() => {
    api.get("/me").then(({ data }) => {
      setMyLetsCallExt(data?.letscall_extension || "");
      setMySignature(data?.assinatura_email || "");
    }).catch(() => {});
  }, []); // null = todos; array = subset (ex: 1 contacto especifico)
  const [contact, setContact] = useState({ nome: "", cargo: "", departamento: "", email: "", telefone: "", telemovel: "", notas: "", principal: false });
  const [drawerContact, setDrawerContact] = useState(null);
  const [editContact, setEditContact] = useState(null);
  const [deal, setDeal] = useState({ nome: "", plataforma: "", estagio: "novo", mrr: 0, partner_id: "" });
  const [activity, setActivity] = useState({ tipo: "nota", assunto: "", corpo: "", contact_id: "", contact_ids: [] });
  const [portalUsers, setPortalUsers] = useState([]);
  const [newUser, setNewUser] = useState({ nome: "", email: "", password: "", enviar_convite: false });
  const [sale, setSale] = useState({ nome: "", preco: 0, override: false, nif: "", morada: "", email_faturacao: "", dpa_aceite: false });
  const [creditHist, setCreditHist] = useState([]);
  const [emails, setEmails] = useState([]);
  const [emailContacts, setEmailContacts] = useState([]); // opcoes por dept
  const [showEmail, setShowEmail] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [emailForm, setEmailForm] = useState({ para: "", assunto: "", html: "" });
  const [templates, setTemplates] = useState([]);
  const [anexos, setAnexos] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [propostaIds, setPropostaIds] = useState([]);
  const [incluirAssinatura, setIncluirAssinatura] = useState(true);
  // Agendamentos: state para modais/accoes
  const [meetingForm, setMeetingForm] = useState(null); // {assunto, inicio, fim, descricao, teams, invite_client}
  const [assignMeeting, setAssignMeeting] = useState(null); // {meeting_id, responsavel_id, invite_client, client_email, contactos}
  const [inviteMeeting, setInviteMeeting] = useState(null); // {meeting, para, assunto, corpo, contactos}
  const [reschedMeeting, setReschedMeeting] = useState(null); // {id, inicio, fim}
  const [teamMembers, setTeamMembers] = useState([]);
  const [followupDias, setFollowupDias] = useState(0);
  const [followups, setFollowups] = useState([]);
  const [emailPreview, setEmailPreview] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [partners, setPartners] = useState([]);
  // Tab Chamadas
  const [chamadas, setChamadas] = useState([]);
  const [chamadasTotal, setChamadasTotal] = useState(0);
  const [chamadasPage, setChamadasPage] = useState(1);
  const [chamadasTotalPages, setChamadasTotalPages] = useState(1);
  const [chamadasLoading, setChamadasLoading] = useState(false);
  const [chamadasFiltroResultado, setChamadasFiltroResultado] = useState("");
  const [chamadasDataInicio, setChamadasDataInicio] = useState("");
  const [chamadasDataFim, setChamadasDataFim] = useState("");
  const [chamadasTranscriptions, setChamadasTranscriptions] = useState({});
  const [chamadasWaOpen, setChamadasWaOpen] = useState(null); // { numero, assunto }
  // Tab Mensagens Agendadas
  const [mensagens, setMensagens] = useState([]);
  const [mensagensTotal, setMensagensTotal] = useState(0);
  const [mensagensLoading, setMensagensLoading] = useState(false);
  const [mensagensFiltroTipo, setMensagensFiltroTipo] = useState("");
  const [mensagensFiltroEstado, setMensagensFiltroEstado] = useState("pendente");

  useEffect(() => {
    api.get("/platforms").then(({ data }) => setPlatforms(data || [])).catch(() => setPlatforms([]));
    api.get("/partners").then(({ data }) => setPartners(data || [])).catch(() => setPartners([]));
  }, []);

  const loadPortalUsers = async () => {
    try { const { data } = await api.get(`/portal-admin/users/${id}`); setPortalUsers(data); } catch (e) { /* noop */ }
  };
  const toggleCancelFlag = async (val) => {
    try {
      await api.put(`/crm/accounts/${id}`, { permitir_cancelamento_recorrente: val });
      toast.success("Definicao de portal atualizada."); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleExemptFlag = async (val) => {
    try {
      await api.put(`/crm/accounts/${id}`, { isento_aprovacao: val });
      toast.success("Definicao de aprovacao atualizada."); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const createPortalUser = async () => {
    if (!newUser.email.trim()) return toast.error("Email obrigatorio.");
    if (!newUser.enviar_convite && newUser.password.length < 8) return toast.error("Password (min. 8) ou ative o convite por email.");
    try {
      const { data } = await api.post("/portal-admin/users", { ...newUser, account_id: id });
      if (data.convite) {
        toast.success(data.convite_enviado ? "Convite enviado por email." : "Acesso criado. Partilhe o link de convite.");
        if (!data.convite_enviado && data.link_convite) navigator.clipboard?.writeText(data.link_convite).catch(() => {});
      } else {
        toast.success("Acesso ao portal criado.");
      }
      setNewUser({ nome: "", email: "", password: "", enviar_convite: false }); loadPortalUsers();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const createSale = async () => {
    if (!sale.nome.trim() || !sale.preco) return toast.error("Indique o item e o preco.");
    const payload = { account_id: id, itens: [{ nome: sale.nome, quantidade: 1, preco: parseFloat(sale.preco) }], override: sale.override };
    if (sale.override) payload.compliance = { nif: sale.nif, morada: sale.morada, email_faturacao: sale.email_faturacao, dpa_aceite: sale.dpa_aceite };
    try {
      const { data } = await api.post("/sales", payload);
      toast.success(data.mensagem || `Venda: ${data.estado}`);
      load(); loadTimeline();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { if (tab === "portal") loadPortalUsers(); }, [tab]); // eslint-disable-line

  const load = async () => {
    try {
      const { data } = await api.get(`/crm/accounts/${id}`);
      setAcc(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const loadTimeline = async () => {
    try {
      const { data } = await api.get(`/crm/accounts/${id}/timeline`);
      setTimeline(data);
    } catch (e) { /* noop */ }
    // Refresca sempre a caixinha de ultima interacao ao carregar timeline
    try {
      const { data } = await api.get(`/crm/accounts/${id}/last-interaction`);
      setLastInter(data && data.em ? data : null);
    } catch (e) { /* noop */ }
  };
  const loadLastInteraction = async () => {
    try {
      const { data } = await api.get(`/crm/accounts/${id}/last-interaction`);
      setLastInter(data && data.em ? data : null);
    } catch (e) { /* noop */ }
  };
  const loadCreditHistory = async () => {
    try { const { data } = await api.get(`/crm/accounts/${id}/credit-history`); setCreditHist(data.meses || []); } catch (e) { /* noop */ }
  };
  const loadEmails = async () => {
    try {
      const { data } = await api.get(`/crm/accounts/${id}/emails`);
      setEmails(data.emails || []);
      setEmailContacts(data.contactos || []);
    } catch (e) { /* noop */ }
  };
  const loadTemplates = async () => {
    try {
      // Junta templates classicos do CRM (email_templates) + templates da plataforma
      // (platform_email_templates gerido em /app/email-templates). Normaliza campos.
      const [crmR, platR] = await Promise.all([
        api.get(`/crm/email-templates?contexto=crm`).catch(() => ({ data: [] })),
        api.get(`/email-templates`).catch(() => ({ data: [] })),
      ]);
      const crmList = (crmR.data || []).map((t) => ({
        chave: t.chave || t.id, nome: t.nome || t.chave,
        assunto: t.assunto || "", html: t.html || "",
        origem: "crm",
        categoria: t.categoria || "crm",
      }));
      const platList = (platR.data || [])
        .filter((t) => (t.canal || "email") === "email" && t.ativo !== false)
        .map((t) => ({
          chave: `plat:${t.id}`, nome: t.nome || t.id,
          assunto: t.assunto || "", html: t.corpo_html || "",
          origem: "plataforma",
          categoria: t.categoria || "custom",
          plataforma: t.plataforma || "",
        }));
      setTemplates([...crmList, ...platList]);
    } catch (e) { /* noop */ }
  };
  const loadProposals = async () => {
    try { const { data } = await api.get(`/cpq/proposals?account_id=${id}`); setProposals(data || []); } catch (e) { /* noop */ }
  };
  const loadFollowups = async () => {
    try { const { data } = await api.get(`/crm/accounts/${id}/followups`); setFollowups(data || []); } catch (e) { /* noop */ }
  };
  const loadTeamMembers = async () => {
    try {
      const { data } = await api.get("/scheduling/config").catch(() => ({ data: null }));
      const inline = (data?.members || []).map((m) => ({ id: m.id || m.email, email: m.email, nome: m.nome || m.email }));
      if (inline.length) { setTeamMembers(inline); return; }
      const u = await api.get("/team/members");
      setTeamMembers((u.data || []).map((m) => ({ id: m.id, email: m.email, nome: m.nome || m.email })));
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); loadTimeline(); loadLastInteraction(); loadCreditHistory(); loadEmails(); loadTemplates(); loadProposals(); loadFollowups(); loadTeamMembers(); }, [id]);
  useAutoRefresh(load); // eslint-disable-line

  const loadChamadas = async (pg, resultado, di, df) => {
    const r = resultado !== undefined ? resultado : chamadasFiltroResultado;
    const dataI = di !== undefined ? di : chamadasDataInicio;
    const dataF = df !== undefined ? df : chamadasDataFim;
    const p = pg || chamadasPage;
    setChamadasLoading(true);
    try {
      const params = new URLSearchParams({ page: p, per_page: 20 });
      if (r) params.set("resultado", r);
      if (dataI) params.set("data_inicio", dataI);
      if (dataF) params.set("data_fim", dataF);
      const { data } = await api.get(`/crm/accounts/${id}/chamadas?${params}`);
      setChamadas(data.items || []);
      setChamadasTotal(data.total || 0);
      setChamadasPage(data.page || 1);
      setChamadasTotalPages(data.total_pages || 1);
    } catch (e) { /* noop */ } finally { setChamadasLoading(false); }
  };

  useEffect(() => { if (tab === "chamadas") loadChamadas(1); }, [tab]); // eslint-disable-line

  const loadMensagens = async (tipo, estado) => {
    const t = tipo !== undefined ? tipo : mensagensFiltroTipo;
    const e = estado !== undefined ? estado : mensagensFiltroEstado;
    setMensagensLoading(true);
    try {
      const params = new URLSearchParams({ account_id: id });
      if (t) params.set("tipo", t);
      if (e) params.set("estado", e);
      const { data } = await api.get(`/scheduled-messages?${params}`);
      setMensagens(data || []);
      setMensagensTotal((data || []).length);
    } catch { /* noop */ } finally { setMensagensLoading(false); }
  };

  useEffect(() => { if (tab === "mensagens") loadMensagens(); }, [tab]); // eslint-disable-line

  // Suporta abertura direta do compose email vindo de Atividades ou de acoes rapidas
  useEffect(() => {
    if (!acc) return;
    // Query params suportados: ?tab=<tab>&compose=1&assunto=&corpo=&contact_id=&novo_contacto=1
    const targetTab = searchParams.get("tab");
    if (targetTab) setTab(targetTab);
    const contactId = searchParams.get("contact_id");
    if (contactId) {
      setTab("contactos");
      setHighlightContactId(contactId);
      setTimeout(() => {
        try {
          document.querySelector(`[data-contact-row-id="${contactId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch { /* noop */ }
        // remover destaque 4s depois
        setTimeout(() => setHighlightContactId(null), 4000);
      }, 250);
    }
    if (searchParams.get("novo_contacto") === "1") {
      setTab("contactos");
      setTimeout(() => setShowContact(true), 150);
    }
    if (searchParams.get("nova_oportunidade") === "1") {
      setTab("oportunidades");
      setTimeout(() => setShowDeal(true), 150);
    }
    if (searchParams.get("nova_reuniao") === "1") {
      setTab("agendamentos");
      setTimeout(() => openMeetingForm(), 200);
    }
    if (searchParams.get("compose") === "1") {
      setTab("emails");
      const assunto = searchParams.get("assunto") || "";
      const corpo = searchParams.get("corpo") || "";
      const emailTo = searchParams.get("email_to") || "";
      // Delay para o loadEmails poder ter chegado
      setTimeout(() => {
        openCompose(emailTo, assunto, corpo ? `<p>${corpo.replace(/\n/g, "<br>")}</p>` : "");
      }, 200);
    }
    const usedKeys = ["compose", "assunto", "corpo", "email_to", "tab", "contact_id", "novo_contacto", "nova_oportunidade", "nova_reuniao"];
    if (usedKeys.some((k) => searchParams.has(k))) {
      const next = new URLSearchParams(searchParams);
      usedKeys.forEach((k) => next.delete(k));
      setSearchParams(next, { replace: true });
    }
  }, [acc, emailContacts.length]); // eslint-disable-line

  const cancelFollowup = async (fid) => {
    try { await api.post(`/crm/accounts/${id}/followups/${fid}/cancel`); toast.success("Follow-up cancelado."); loadFollowups(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openCompose = (para = "", assunto = "", html = "") => {
    // Se nao especificado, escolhe o contacto principal como destinatario por defeito
    let dest = para;
    if (!dest && emailContacts && emailContacts.length) {
      const principal = emailContacts.find((c) => c.principal) || emailContacts[0];
      dest = principal.email || "";
    }
    setEmailForm({ para: dest, assunto, html });
    setAnexos([]); setPropostaIds([]); setIncluirAssinatura(true); setFollowupDias(0); setSelectedTemplate("");
    setShowEmail(true);
  };
  const quotedBody = (e) => {
    const quoted = (e.corpo || "").split("\n").map((l) => `&gt; ${l}`).join("<br>");
    const cabecalho = `<p><br></p><p>------- Mensagem original -------</p>`
      + `<p><b>De:</b> ${e.de || "-"}<br><b>Data:</b> ${e.em || ""}<br><b>Assunto:</b> ${e.assunto || ""}</p>`;
    return `<p></p>${cabecalho}<blockquote>${quoted}</blockquote>`;
  };
  const replyEmail = (e) => {
    const s = e.assunto || "";
    const assunto = s.toLowerCase().startsWith("re:") ? s : `RE: ${s}`;
    openCompose(e.de || "", assunto, quotedBody(e));
  };
  const buildTemplateVars = (destPara = "") => {
    if (!acc) return {};
    const contactos = acc.contactos || [];
    // Escolhe o contacto destinatario preferido: 1) que bate com email destino,
    // 2) contacto principal, 3) primeiro contacto disponivel.
    const emailDest = (destPara || "").split(",")[0].trim().toLowerCase();
    const dest = emailDest && contactos.find((c) => ((c.emails || []).some((e) => (e || "").toLowerCase() === emailDest) || (c.email || "").toLowerCase() === emailDest))
      || contactos.find((c) => c.principal)
      || contactos[0] || {};
    // Subscricao / plataforma / MRR: usa a subscricao activa mais recente se existir.
    const subs = (acc.subscricoes || []).filter((s) => (s.estado || "").toLowerCase() === "ativa");
    const sub = subs[0] || (acc.subscricoes || [])[0] || {};
    const contrato = (acc.contratos || [])[0] || {};
    const fatura = (acc.faturas || [])[0] || {};
    const hoje = new Date();
    const fmt = (d) => {
      if (!d) return "";
      try { return new Date(d).toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
      catch { return String(d); }
    };
    const moeda = (n, m = "EUR") => (typeof n === "number" ? `${n.toFixed(2).replace(".", ",")} ${m}` : "");
    return {
      cliente_nome: dest.nome || acc.nome || "",
      cliente_nif: acc.nif || "",
      cliente_email: (dest.emails || [dest.email])[0] || acc.email || "",
      empresa: acc.nome || "",
      plataforma: sub.plataforma || sub.produto_nome || "",
      mrr: moeda(sub.mrr, sub.moeda || "EUR"),
      valor_total: moeda(sub.valor_total, sub.moeda || "EUR"),
      moeda: sub.moeda || "EUR",
      data_inicio: fmt(sub.inicio),
      data_fim: fmt(sub.fim),
      data_renovacao: fmt(sub.proxima_renovacao || sub.fim),
      data_cancelamento: fmt(sub.cancelada_em),
      numero_contrato: contrato.numero || contrato.id || "",
      numero_fatura: fatura.numero || fatura.id || "",
      responsavel: acc.owner_email || "",
      hoje: fmt(hoje),
      link_portal: window.location.origin + "/portal",
      link_pagamento: fatura.link_pagamento || "",
      cargo: dest.cargo || "",
      departamento: dest.departamento || "",
      telefone: (dest.telefones || [])[0] || dest.telefone || dest.telemovel || "",
      agente_nome: (user && (user.nome || user.email)) || "",
    };
  };
  const renderPlaceholders = (text, vars) => {
    if (!text) return text;
    let out = text.replace(/\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g, (m, key) => {
      const v = vars[key];
      return (v === undefined || v === null || v === "") ? m : String(v);
    });
    // Templates antigos (ex: handoff_response) usam chaves simples {var} em vez de {{var}}
    out = out.replace(/\{\s*([a-zA-Z0-9_.-]+)\s*\}/g, (m, key) => {
      const v = vars[key];
      return (v === undefined || v === null || v === "") ? m : String(v);
    });
    return out;
  };
  const applyTemplate = (chave) => {
    const t = templates.find((x) => x.chave === chave);
    if (!t) return;
    const vars = buildTemplateVars(emailForm.para);
    setEmailForm((f) => ({
      ...f,
      assunto: renderPlaceholders(t.assunto || f.assunto, vars),
      html: renderPlaceholders(t.html || f.html, vars),
    }));
    setSelectedTemplate(chave);
  };
  const onAnexos = async (files) => {
    const arr = [];
    for (const file of Array.from(files || [])) {
      if (file.size > 7 * 1024 * 1024) { toast.error(`Anexo '${file.name}' excede 7MB.`); continue; }
      const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1] || ""); r.readAsDataURL(file); });
      arr.push({ filename: file.name, content: b64, type: file.type || "application/octet-stream" });
    }
    setAnexos((prev) => [...prev, ...arr]);
  };

  const sendEmail = async () => {
    if (!emailForm.assunto.trim()) return toast.error("Indique o assunto.");
    try {
      const { data } = await api.post(`/crm/accounts/${id}/emails`, {
        para: emailForm.para, assunto: emailForm.assunto, html: emailForm.html, attachments: anexos,
        incluir_assinatura: incluirAssinatura, followup_dias: Number(followupDias) || 0, proposta_ids: propostaIds,
      });
      if (data.estado === "enviado") toast.success("Email enviado.");
      else if (data.estado === "nao_configurado") toast.message("SendGrid nao configurado - o email foi registado mas nao enviado.");
      else toast.message(`Estado do envio: ${data.estado}${data.erro ? " - " + data.erro : ""}`);
      setShowEmail(false); setEmailForm({ para: "", assunto: "", html: "" }); setAnexos([]); setPropostaIds([]); setFollowupDias(0); setSelectedTemplate("");
      loadEmails(); loadTimeline(); loadFollowups();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addContact = async () => {
    if (!contact.nome.trim()) return toast.error("Indique o nome.");
    try {
      const telefones = [contact.telefone, contact.telemovel].filter((x) => (x || "").trim());
      await api.post("/crm/contacts", {
        account_id: id,
        nome: contact.nome,
        cargo: contact.cargo,
        departamento: contact.departamento,
        email: contact.email,
        emails: contact.email ? [contact.email] : [],
        telefone: contact.telefone,
        telemovel: contact.telemovel,
        telefones,
        principal: !!contact.principal,
        notas: contact.notas,
      });
      toast.success("Contacto adicionado.");
      setShowContact(false);
      setContact({ nome: "", cargo: "", departamento: "", email: "", telefone: "", telemovel: "", notas: "", principal: false });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openEditContact = (c) => {
    setEditContact({
      ...c,
      email: c.email || (c.emails || [])[0] || "",
      telefone: c.telefone || (c.telefones || [])[0] || "",
      telemovel: c.telemovel || (c.telefones || [])[1] || "",
    });
  };

  const saveEditContact = async () => {
    if (!editContact?.nome?.trim()) return toast.error("Nome obrigatorio.");
    try {
      const telefones = [editContact.telefone, editContact.telemovel].filter((x) => (x || "").trim());
      await api.put(`/crm/contacts/${editContact.id}`, {
        nome: editContact.nome, cargo: editContact.cargo || "",
        departamento: editContact.departamento || "",
        email: editContact.email, emails: editContact.email ? [editContact.email] : [],
        telefone: editContact.telefone, telemovel: editContact.telemovel, telefones,
        notas: editContact.notas || "", principal: !!editContact.principal,
        marketing_optin: !!editContact.marketing_optin,
      });
      toast.success("Contacto actualizado.");
      setEditContact(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addDeal = async () => {
    if (!deal.nome.trim()) return toast.error("Indique o nome da oportunidade.");
    try {
      await api.post("/crm/deals", { ...deal, account_id: id, mrr: parseFloat(deal.mrr) || 0 });
      toast.success("Oportunidade criada.");
      setShowDeal(false); setDeal({ nome: "", plataforma: "", estagio: "novo", mrr: 0, partner_id: "" });
      load(); loadTimeline();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addActivity = async () => {
    try {
      const cids = (activity.contact_ids && activity.contact_ids.length ? activity.contact_ids : (activity.contact_id ? [activity.contact_id] : []));
      // Converter followup_dt (datetime-local) para ISO UTC
      let followup_dt = "";
      if (activity.tipo === "chamada" && activity.followup_dt && activity.resultado && activity.resultado !== "atendeu") {
        try { followup_dt = new Date(activity.followup_dt).toISOString(); } catch { followup_dt = activity.followup_dt; }
      }
      const payload = { ...activity, account_id: id, contact_id: cids[0] || "", contact_ids: cids, followup_dt };
      const { data } = await api.post("/crm/activities", payload);
      if (data?.created_task) {
        toast.success(`Atividade registada + tarefa de recontacto agendada para ${(data.created_task.prazo || "").slice(0,16).replace("T"," ")}.`);
      } else {
        toast.success("Atividade registada.");
      }
      setShowActivity(false); setActivity({ tipo: "nota", assunto: "", corpo: "", contact_id: "", contact_ids: [], resultado: "", followup_dt: "", followup_notas: "" });
      loadTimeline();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const changeStage = async (dealId, estagio) => {
    try {
      const { data } = await api.put(`/crm/deals/${dealId}`, { estagio });
      if (data?.auto_proposal) {
        const p = data.auto_proposal;
        toast.success(`Proposta rascunho ${p.reused ? "existente" : "criada"}: ${p.numero}`, {
          action: {
            label: "Pre-visualizar",
            onClick: () => setPreviewProp({ id: p.id, numero: p.numero, titulo: p.titulo, account_id: id, estado: "rascunho" }),
          },
          duration: 10000,
        });
      } else {
        toast.success("Estagio atualizado.");
      }
      load(); loadTimeline();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // ---- Agendamentos: helpers ---------------------------------------------
  const primaryContactEmail = () => {
    const principal = (emailContacts || []).find((c) => c.principal) || (emailContacts || [])[0];
    return principal?.email || "";
  };
  const openMeetingForm = (base) => {
    // Novo agendamento: pre-preenche assunto com o nome da conta e o convidado com o principal
    const start = new Date(); start.setMinutes(0, 0, 0); start.setHours(start.getHours() + 1);
    const end = new Date(start); end.setHours(end.getHours() + 1);
    setMeetingForm({
      assunto: base?.assunto || `Reuniao ${acc?.nome || ""}`.trim(),
      inicio: base?.inicio || start.toISOString().slice(0, 16),
      fim: base?.fim || end.toISOString().slice(0, 16),
      descricao: base?.descricao || "",
      teams: true,
      client_email: base?.client_email || primaryContactEmail(),
      invite_client: true,
    });
  };
  const saveMeeting = async () => {
    if (!meetingForm?.assunto || !meetingForm?.inicio || !meetingForm?.fim) return toast.error("Assunto, inicio e fim sao obrigatorios.");
    try {
      const participantes = meetingForm.invite_client && meetingForm.client_email ? [meetingForm.client_email] : [];
      const iso = (v) => (v && !v.endsWith("Z") && v.length <= 19 ? new Date(v).toISOString() : v);
      await api.post("/scheduling/meetings", {
        assunto: meetingForm.assunto,
        inicio: iso(meetingForm.inicio),
        fim: iso(meetingForm.fim),
        descricao: meetingForm.descricao || "",
        account_id: id,
        participantes,
        teams: !!meetingForm.teams,
        client_email: meetingForm.client_email || "",
      });
      toast.success("Agendamento criado.");
      setMeetingForm(null);
      load(); loadTimeline();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const confirmMeeting = async (m) => {
    try {
      await api.put(`/scheduling/meetings/${m.id}`, { estado: "confirmada" });
      toast.success("Agendamento confirmado.");
      load(); loadTimeline();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const cancelMeeting = async (m) => {
    if (!window.confirm(`Cancelar "${m.assunto}"?`)) return;
    try {
      await api.delete(`/scheduling/meetings/${m.id}`);
      toast.success("Agendamento cancelado."); load(); loadTimeline();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const removeMeeting = async (m) => {
    if (!window.confirm(`Eliminar definitivamente "${m.assunto}"?`)) return;
    try {
      await api.delete(`/scheduling/meetings/${m.id}?hard=true`);
      toast.success("Eliminado."); load(); loadTimeline();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openAssignMeeting = (m) => {
    // Herda client_email do proprio agendamento (nao substitui pelo principal)
    setAssignMeeting({
      meeting_id: m.id,
      responsavel_id: m.assigned_to || "",
      invite_client: true,
      client_email: m.client_email || primaryContactEmail(),
    });
  };
  const doAssign = async () => {
    const resp = teamMembers.find((u) => u.id === assignMeeting.responsavel_id);
    if (!resp) return toast.error("Escolha um membro da equipa.");
    try {
      await api.patch(`/scheduling/meetings/${assignMeeting.meeting_id}/assign`, {
        assigned_to: resp.id, assigned_email: resp.email, assigned_nome: resp.nome || resp.email,
        invite_client: !!assignMeeting.invite_client,
        client_email: assignMeeting.client_email || "",
      });
      toast.success(`Atribuido a ${resp.nome || resp.email}${assignMeeting.invite_client ? " (cliente convidado)" : ""}.`);
      setAssignMeeting(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openInviteMeeting = (m) => {
    const dataInicio = m.inicio ? new Date(m.inicio).toLocaleString("pt-PT") : "";
    const dataFim = m.fim ? new Date(m.fim).toLocaleString("pt-PT") : "";
    const linkTeams = m.join_url ? `\n\nLink Microsoft Teams:\n${m.join_url}` : "";
    const corpo = `Ola,\n\nConfirmamos o agendamento da reuniao "${m.assunto}".\n\nInicio: ${dataInicio}\nFim: ${dataFim}${linkTeams}\n\nSe precisar de reagendar, responda a este email.\n\nCumprimentos,\nEquipa Cogniseg`;
    setInviteMeeting({
      meeting: m,
      para: m.client_email || primaryContactEmail(),
      assunto: `Convite: ${m.assunto}`,
      corpo,
    });
  };
  const sendInvite = async () => {
    if (!inviteMeeting?.para) return toast.error("Indique o destinatario.");
    try {
      const html = (inviteMeeting.corpo || "").split("\n").map((l) => `<p>${l}</p>`).join("");
      const { data } = await api.post(`/scheduling/meetings/${inviteMeeting.meeting.id}/send-invite-email`, {
        para: inviteMeeting.para, assunto: inviteMeeting.assunto, html,
        incluir_teams_link: false, incluir_data: false,
      });
      if (data?.estado === "enviado") toast.success("Convite enviado por email.");
      else toast.message(`Estado do envio: ${data?.estado || "?"}${data?.erro ? " - " + data.erro : ""}`);
      setInviteMeeting(null); loadTimeline();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openReschedule = (m) => {
    setReschedMeeting({ id: m.id, inicio: (m.inicio || "").slice(0, 16), fim: (m.fim || "").slice(0, 16) });
  };
  const saveReschedule = async () => {
    if (!reschedMeeting.inicio || !reschedMeeting.fim) return toast.error("Indique inicio e fim.");
    try {
      await api.put(`/scheduling/meetings/${reschedMeeting.id}`, {
        inicio: new Date(reschedMeeting.inicio).toISOString(),
        fim: new Date(reschedMeeting.fim).toISOString(),
      });
      toast.success("Agendamento reagendado. Convite Teams actualizado.");
      setReschedMeeting(null); load(); loadTimeline();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Card de agendamento com toda a barra de accoes (inline)
  const MeetingCard = ({ r, variant }) => {
    const isPending = variant === "pending" || r.estado === "provisorio" || (r.origem || "").includes("formulario");
    const badgeClass = r.estado === "confirmada" ? "bg-green-50 text-green-700 border-green-300"
      : r.estado === "cancelada" ? "bg-red-50 text-red-700 border-red-300"
      : "bg-amber-50 text-amber-700 border-amber-300";
    return (
      <div className={`rounded-lg p-3 ${isPending ? "border border-amber-200 bg-amber-50/40" : "border border-tint bg-white"}`} data-testid={`meeting-${r.id}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-navy">{r.assunto}</div>
            <div className="text-[11px] text-muted2 mt-0.5">
              {dt(r.inicio)} {r.fim ? `- ${dt(r.fim).split(" ").slice(-1)[0]}` : ""}
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 border ${badgeClass}`}>{r.estado || "provisorio"}</span>
              {(r.origem || "").includes("formulario") && <span className="text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 border bg-blue/10 text-blue border-blue/30">pedido do site</span>}
              {r.assigned_nome && <span className="text-[10px] text-slate-600">Resp.: {r.assigned_nome}</span>}
              {r.client_email && <span className="text-[10px] text-slate-600">Convidado: {r.client_email}</span>}
            </div>
          </div>
        </div>
        {r.descricao && <div className="text-[11px] text-slate-600 mt-2 whitespace-pre-wrap border-t border-slate-200 pt-2">{r.descricao}</div>}
        {r.join_url && <a href={r.join_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-blue text-xs hover:underline" data-testid={`meeting-teams-${r.id}`}><Video size={12} /> Abrir link Teams</a>}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {isPending && r.estado !== "confirmada" && r.estado !== "cancelada" && (
            <button onClick={() => confirmMeeting(r)} data-testid={`meeting-confirm-${r.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-md border border-green-500 text-green-700 bg-white hover:bg-green-50 px-2 py-1"><CheckCircle2 size={12} /> Confirmar</button>
          )}
          <button onClick={() => openAssignMeeting(r)} data-testid={`meeting-assign-${r.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-md border border-tint text-navy hover:bg-slate-50 px-2 py-1"><UserCheck size={12} /> Atribuir</button>
          <button onClick={() => openInviteMeeting(r)} data-testid={`meeting-invite-${r.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-md border border-tint text-navy hover:bg-slate-50 px-2 py-1"><Send size={12} /> Enviar convite</button>
          <button onClick={() => openReschedule(r)} data-testid={`meeting-reschedule-${r.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-md border border-tint text-navy hover:bg-slate-50 px-2 py-1"><CalendarClock size={12} /> Reagendar</button>
          {r.estado !== "cancelada" && (
            <button onClick={() => cancelMeeting(r)} data-testid={`meeting-cancel-${r.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-md border border-amber-500 text-amber-700 bg-white hover:bg-amber-50 px-2 py-1"><XCircle size={12} /> Cancelar</button>
          )}
          {isSuper && (
            <button onClick={() => removeMeeting(r)} data-testid={`meeting-delete-${r.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-md border border-red-500 text-red-700 bg-white hover:bg-red-50 px-2 py-1"><Trash2 size={12} /> Eliminar</button>
          )}
        </div>
      </div>
    );
  };
  // -----------------------------------------------------------------------

  const deleteAccount = async () => {
    if (!window.confirm(`Eliminar definitivamente "${acc.nome}" e todos os registos associados (contactos, negocios, atividades, reunioes e chats)? Esta accao nao pode ser revertida.`)) return;
    try {
      await api.delete(`/crm/accounts/${id}`);
      toast.success("Lead/conta eliminada.");
      nav("/app/crm/contas");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleArchive = async () => {
    if (acc.arquivada) {
      try {
        await api.post(`/crm/accounts/${id}/unarchive`);
        toast.success("Lead desarquivada.");
        load();
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    } else {
      setArchiveModal({ motivo: "perdida", nota: "" });
    }
  };

  const confirmArchive = async () => {
    const label = ARCHIVE_REASONS.find((x) => x.value === archiveModal.motivo)?.label || archiveModal.motivo;
    const motivo = archiveModal.nota.trim() ? `${label} - ${archiveModal.nota.trim()}` : label;
    try {
      await api.post(`/crm/accounts/${id}/archive`, { motivo });
      toast.success("Lead arquivada. Deixa de aparecer na listagem por defeito.");
      setArchiveModal(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!acc) return <><div className="text-muted2">A carregar...</div></>;

  const TABS = [
    { key: "resumo", label: "Resumo" },
    { key: "contactos", label: `Contactos (${(acc.contactos || []).length})` },
    { key: "agendamentos", label: `Agendamentos (${(acc.reunioes || []).length})` },
    { key: "chats", label: `Chats AI (${(acc.chats || []).length})` },
    { key: "faturacao", label: "Faturacao" },
    { key: "comercial", label: "Comercial" },
    { key: "subscricoes", label: `Subscricoes (${(acc.subscricoes || []).length})` },
    { key: "oportunidades", label: `Oportunidades (${(acc.oportunidades || []).length})` },
    { key: "chamadas", label: chamadasTotal > 0 ? `Chamadas (${chamadasTotal})` : "Chamadas" },
    { key: "mensagens", label: "Mensagens" },
    { key: "emails", label: `Emails (${emails.length})` },
    { key: "marketing", label: "Marketing" },
    { key: "timeline", label: "Timeline" },
    { key: "portal", label: "Portal & Venda" },
  ];

  return (
    <>
      <button onClick={() => nav("/app/crm/contas")} className="flex items-center gap-1 text-sm text-blue mb-4 hover:underline" data-testid="back-to-accounts">
        <ArrowLeft size={16} /> Contas
      </button>

      {archiveModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="archive-modal" {...overlayCloseHandlers(() => setArchiveModal(null))}>
          <div className="bg-white rounded-xl p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-navy font-bold text-lg mb-1">Arquivar lead</h3>
            <p className="text-xs text-muted2 mb-4">Escolha o motivo. A lead deixa de aparecer na listagem, mas nao e eliminada.</p>
            <label className="block text-sm font-semibold text-navy mb-1">Motivo</label>
            <select value={archiveModal.motivo} onChange={(e) => setArchiveModal({ ...archiveModal, motivo: e.target.value })}
              data-testid="archive-reason" className="w-full px-3 py-2 border border-tint rounded-lg bg-white mb-3">
              {ARCHIVE_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <label className="block text-sm font-semibold text-navy mb-1">Nota (opcional)</label>
            <input value={archiveModal.nota} onChange={(e) => setArchiveModal({ ...archiveModal, nota: e.target.value })}
              data-testid="archive-note" placeholder="Detalhe adicional" className="w-full px-3 py-2 border border-tint rounded-lg mb-4" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setArchiveModal(null)} className="text-sm text-muted2 border border-tint rounded-lg px-3 py-2 hover:bg-tint">Cancelar</button>
              <button onClick={confirmArchive} data-testid="archive-confirm" className="text-sm text-white bg-navy rounded-lg px-4 py-2 hover:opacity-90">Arquivar</button>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-tint flex items-center justify-center"><Building2 className="text-blue" /></div>
          <div>
            <h1 className="text-navy text-3xl font-bold tracking-tight leading-none" data-testid="account-title">{acc.nome}</h1>
            <div className="text-sm text-muted2 mt-1 flex items-center gap-2">
              <Badge value={acc.tipo} /> {acc.nif ? `NIF ${acc.nif}` : "Sem NIF"} {acc.setor ? `- ${acc.setor}` : ""}
              {acc.cae && <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-teal-50 border border-teal-200 text-teal-700 text-[11px] font-mono font-semibold" data-testid="account-cae-badge">CAE {acc.cae}</span>}
              {acc.arquivada && <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-[11px] font-semibold" data-testid="account-archived-badge">Arquivada{acc.arquivo_motivo ? `: ${acc.arquivo_motivo}` : ""}</span>}
            </div>
            {acc.arquivada && (acc.arquivada_em || acc.arquivada_por) && (
              <div className="text-[11px] text-muted2 mt-1" data-testid="account-archived-info">
                Arquivada em {acc.arquivada_em ? new Date(acc.arquivada_em).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : "-"}{acc.arquivada_por ? ` por ${acc.arquivada_por}` : ""}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => nav(`/app/crm/contas?edit_id=${id}`)} data-testid="edit-account-btn" title="Editar dados da ficha"
            className="inline-flex items-center gap-1.5 text-sm text-navy border border-tint hover:bg-tint rounded-lg px-3 py-2 transition-colors">
            <Pencil size={15} /> Editar
          </button>
          <button onClick={() => setShowSms(true)} data-testid="send-sms-btn" title="Enviar SMS"
            className="inline-flex items-center gap-1.5 text-sm text-navy border border-tint hover:bg-tint rounded-lg px-3 py-2 transition-colors">
            <MessageSquare size={15} /> Enviar SMS
          </button>
          <button onClick={() => setShowPhonePicker(true)} data-testid="ligar-top-btn" title="Ligar (LetsCall)"
            className="inline-flex items-center gap-1.5 text-sm text-navy border border-tint hover:bg-tint rounded-lg px-3 py-2 transition-colors">
            <Phone size={15} /> Ligar
          </button>
          <button onClick={toggleArchive} data-testid="archive-account-btn" title={acc.arquivada ? "Desarquivar lead" : "Arquivar lead"}
            className="inline-flex items-center gap-1.5 text-sm text-navy border border-tint hover:bg-tint rounded-lg px-3 py-2 transition-colors">
            {acc.arquivada ? <><ArchiveRestore size={15} /> Desarquivar</> : <><Archive size={15} /> Arquivar</>}
          </button>
          {isSuper && (
            <button onClick={deleteAccount} data-testid="delete-account-btn" title="Eliminar lead/conta (super admin)"
              className="inline-flex items-center gap-1.5 text-sm text-red-600 border border-red-200 hover:bg-red-50 rounded-lg px-3 py-2 transition-colors">
              <X size={15} /> Eliminar lead
            </button>
          )}
        </div>
      </div>

      {lastInter && (
        <div className="mb-4 border border-tint rounded-lg bg-slate-50 px-4 py-2.5 flex items-center gap-3" data-testid="last-interaction">
          <Clock size={14} className="text-blue shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Ultima interacao</div>
            <div className="text-sm text-navy truncate" data-testid="last-interaction-title">
              <span className="font-semibold">{lastInter.titulo || lastInter.tipo}</span>
              {lastInter.detalhe && <span className="text-slate-600"> - {lastInter.detalhe}</span>}
            </div>
          </div>
          <div className="text-[11px] text-slate-500 shrink-0" data-testid="last-interaction-time">
            {lastInter.em ? new Date(lastInter.em).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : "-"}
          </div>
        </div>
      )}

      {acc.bant_timing && <TimingWidget acc={acc} onAction={() => setTab("resumo")} />}

      {acc.divida_aberta > 0 && (
        <div data-testid="credit-warning" className={`mb-6 flex items-start gap-3 rounded-lg p-4 border ${acc.excede_credito ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`}>
          <AlertTriangle className={`mt-0.5 shrink-0 ${acc.excede_credito ? "text-red-600" : "text-amber-600"}`} size={20} />
          <div className="flex-1 text-sm">
            <div className={`font-semibold ${acc.excede_credito ? "text-red-700" : "text-amber-700"}`}>
              {acc.excede_credito ? "Limite de credito excedido" : "Divida em aberto"}
            </div>
            <div className={acc.excede_credito ? "text-red-600" : "text-amber-700"}>
              Este cliente tem {eur(acc.divida_aberta)} em faturas por liquidar
              {acc.limite_credito_efetivo ? ` (limite ${eur(acc.limite_credito_efetivo)})` : ""}.
              {acc.excede_credito ? " Reveja a cobranca antes de avancar com novas vendas." : ""}
            </div>
          </div>
        </div>
      )}

      {creditHist.some((m) => m.valor > 0) && (
        <div data-testid="credit-history" className="mb-6 border border-tint rounded-xl p-5 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-navy text-sm">Evolucao da divida em aberto (6 meses)</h3>
            <span className="text-xs text-muted2">fim de cada mes</span>
          </div>
          {(() => {
            const max = Math.max(...creditHist.map((m) => m.valor), 1);
            return (
              <div className="flex items-end gap-3 h-32">
                {creditHist.map((m, i) => {
                  const h = Math.round((m.valor / max) * 100);
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end h-full" data-testid={`credit-bar-${m.mes}`}>
                      <span className="text-[10px] text-navy font-semibold mb-1">{m.valor > 0 ? eur(m.valor) : ""}</span>
                      <div className="w-full rounded-t-md bg-blue/80 transition-all" style={{ height: `${Math.max(h, m.valor > 0 ? 6 : 0)}%` }} />
                      <span className="text-[10px] text-muted2 mt-1">{m.mes.slice(5)}/{m.mes.slice(2, 4)}</span>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      <div className="flex gap-1 border-b border-tint mb-6">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.key ? "border-blue text-blue" : "border-transparent text-muted2 hover:text-navy"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "resumo" && (
        <div className="space-y-4" data-testid="tab-content-resumo">
          <SectionBox title="Identificacao">
            <FGrid>
              <F label="Nome" v={acc.nome} />
              <F label="NIF" v={acc.nif} />
              <F label="Website" v={acc.website ? <a href={acc.website} target="_blank" rel="noreferrer" className="text-blue hover:underline">{acc.website}</a> : "-"} />
              <F label="Email geral" v={acc.email ? <a href={`mailto:${acc.email}`} className="text-blue hover:underline">{acc.email}</a> : "-"} />
              <F label="Telefone" v={acc.telefone} />
              <F label="Morada" v={[acc.morada, acc.codigo_postal, acc.localidade || acc.cidade, acc.pais].filter(Boolean).join(", ")} />
            </FGrid>
          </SectionBox>
          <SectionBox title="Classificacao e dimensao">
            <FGrid>
              <F label="Tipo" v={<Badge value={acc.tipo} />} />
              <F label="Origem" v={acc.origem} />
              <F label="Setor / industria" v={acc.setor} />
              <F label="Area de negocio" v={acc.area_negocio} />
              <F label="Dimensao" v={acc.dimensao} />
              <F label="CAE" v={acc.cae ? <span className="font-mono font-semibold text-teal-700 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded text-[12px]">{acc.cae}</span> : "-"} />
              <F label="Colaboradores" v={acc.numero_colaboradores} />
              <F label="Volume de negocios" v={acc.volume_negocios ? eur(acc.volume_negocios) : "-"} />
              <F label="Grupo empresarial" v={acc.grupo} />
              <F label="Rating" v={acc.rating} />
              <F label="Prioridade" v={<Badge value={acc.prioridade || "media"} />} />
              <F label="Ciclo de vida" v={acc.ciclo_vida} />
              <F label="Cliente desde" v={acc.cliente_desde ? dt(acc.cliente_desde) : "-"} />
              <F label="Canal preferido" v={acc.canal_preferido} />
              <F label="Idioma / fuso" v={`${acc.idioma || "pt"} - ${acc.fuso || "-"}`} />
              <F label="Criado em" v={dt(acc.criado_em)} />
              <F label="Criado por" v={acc.criado_por_nome || acc.criado_por_email || (acc.origem && acc.origem !== "manual" ? `Automatico (${acc.origem})` : "-")} />
            </FGrid>
          </SectionBox>
          {(acc.tags || []).length > 0 && (
            <SectionBox title="Tags">
              <div className="flex flex-wrap gap-1">{acc.tags.map((t) => <span key={t} className="inline-flex text-[11px] px-2 py-0.5 rounded-full bg-blue/10 text-blue border border-blue/30">{t}</span>)}</div>
            </SectionBox>
          )}
          {acc.notas && <SectionBox title="Notas"><p className="text-sm text-navy whitespace-pre-wrap">{acc.notas}</p></SectionBox>}

          {/* Atividade recente - ultimas 5 entradas do timeline com link para tab completo */}
          <SectionBox title="Atividade recente"
            action={timeline.length > 0 && (
              <button onClick={() => setTab("timeline")} data-testid="resumo-timeline-link"
                className="text-[11px] font-semibold text-blue hover:underline">Ver timeline completa ({timeline.length})</button>
            )}>
            {timeline.length === 0 ? (
              <div className="text-sm text-muted2 py-2">Sem atividade registada.</div>
            ) : (
              <div className="space-y-1.5" data-testid="resumo-timeline-preview">
                {timeline.slice(0, 5).map((it, i) => {
                  const Icon = TIPO_ICON[it.tipo] || StickyNote;
                  const callMeta = CALL_RESULT_META[it.resultado];
                  return (
                    <div key={i} className="flex items-start gap-3 py-1.5 border-b border-tint last:border-b-0" data-testid={`resumo-timeline-item-${i}`}>
                      {callMeta ? (
                        <div className={`mt-0.5 w-7 h-7 rounded-full ${callMeta.dot} ring-2 ${callMeta.ring} flex items-center justify-center shrink-0`}
                          title={callMeta.label} data-testid={`resumo-timeline-call-result-${i}`}>
                          <callMeta.icon size={14} className="text-white" />
                        </div>
                      ) : (
                        <div className="mt-0.5 w-6 h-6 rounded-full bg-blue/10 flex items-center justify-center shrink-0">
                          <Icon size={12} className="text-blue" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-navy truncate">{it.titulo}</div>
                        <div className="text-[10px] text-muted2">{it.tipo.replace(/[:_]/g, " ")}</div>
                        {callMeta && (
                          <span className={`inline-flex items-center text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border mt-0.5 ${callMeta.badge}`}>
                            {callMeta.label}
                          </span>
                        )}
                        {it.contactos && it.contactos.length > 0 && (
                          <div className="mt-0.5 flex flex-wrap gap-1" data-testid={`resumo-timeline-contacts-${i}`}>
                            {it.contactos.slice(0, 3).map((c) => (
                              <span key={c.id} title={c.email || c.nome}
                                className="inline-flex items-center gap-0.5 text-[9px] bg-blue/5 text-navy px-1.5 py-0.5 rounded border border-blue/20">
                                <span className="w-3 h-3 rounded-full bg-blue text-white flex items-center justify-center text-[7px] font-bold">
                                  {(c.nome || "?").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase() || "?"}
                                </span>
                                <span>{c.nome}</span>
                              </span>
                            ))}
                            {it.contactos.length > 3 && <span className="text-[9px] text-muted2">+{it.contactos.length - 3}</span>}
                          </div>
                        )}
                        {it.detalhe && <div className="text-xs text-slate-600 truncate">{it.detalhe}</div>}
                      </div>
                      <div className="text-[11px] text-muted2 shrink-0 whitespace-nowrap">{dt(it.em)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </SectionBox>
        </div>
      )}

      {tab === "faturacao" && (
        <div className="space-y-4" data-testid="tab-content-faturacao">
          <SectionBox title="Dados de faturacao">
            <FGrid>
              <F label="Faturar em nome de" v={acc.fatura_a_nome_de || acc.nome} />
              <F label="NIF" v={acc.nif} />
              <F label="Morada de faturacao" v={[acc.morada_faturacao, acc.codigo_postal_faturacao, acc.localidade_faturacao || acc.cidade_faturacao, acc.pais_faturacao].filter(Boolean).join(", ") || "usa morada principal"} />
              <F label="Email de faturacao" v={acc.contacto_faturacao_email ? <a href={`mailto:${acc.contacto_faturacao_email}`} className="text-blue hover:underline">{acc.contacto_faturacao_email}</a> : "-"} />
              <F label="IBAN" v={acc.iban ? <code className="text-xs bg-slate-100 rounded px-1.5 py-0.5">{acc.iban}</code> : "-"} />
              <F label="SWIFT / BIC" v={acc.swift} />
              <F label="Metodo pagamento preferido" v={acc.metodo_pagamento_pref} />
              <F label="Condicoes de pagamento" v={acc.condicoes_pagamento || "30 dias"} />
              <F label="Moeda" v={acc.moeda || "EUR"} />
              <F label="Regime IVA" v={acc.iva_regime} />
              <F label="Taxa IVA" v={acc.taxa_iva != null ? `${acc.taxa_iva} %` : "-"} />
              <F label="Retencao IR" v={acc.retencao_ir ? `${acc.retencao_ir} %` : "-"} />
              <F label="Limite de credito" v={acc.limite_credito ? eur(acc.limite_credito) : "-"} />
              <F label="Divida em aberto" v={acc.divida_aberta ? eur(acc.divida_aberta) : "-"} />
              <F label="Estado credito" v={acc.credito_estado ? <Badge value={acc.credito_estado} /> : "-"} />
            </FGrid>
          </SectionBox>
        </div>
      )}

      {tab === "comercial" && (
        <div className="space-y-4" data-testid="tab-content-comercial">
          <SectionBox title="Metricas comerciais">
            <FGrid>
              <F label="Responsavel" v={acc.responsavel_nome || acc.responsavel_id || "-"} />
              <F label="Health score" v={acc.health_score != null ? `${acc.health_score}/100` : "-"} />
              <F label="NPS" v={acc.nps != null ? acc.nps : "-"} />
              <F label="Rating" v={acc.rating} />
            </FGrid>
          </SectionBox>
          <SectionBox title="Potencial">
            <div className="grid md:grid-cols-2 gap-3">
              <div className="border border-tint rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Upsell</div>
                <p className="text-sm text-navy whitespace-pre-wrap">{acc.upsell_potencial || <span className="text-slate-400 italic">Sem notas de upsell.</span>}</p>
              </div>
              <div className="border border-tint rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Cross-sell</div>
                <p className="text-sm text-navy whitespace-pre-wrap">{acc.cross_sell_potencial || <span className="text-slate-400 italic">Sem notas de cross-sell.</span>}</p>
              </div>
            </div>
          </SectionBox>
        </div>
      )}

      {tab === "subscricoes" && (
        <div className="space-y-4" data-testid="tab-content-subscricoes">
          <SectionBox title={`Subscricoes ativas (${(acc.subscricoes || []).length})`}>
            {(!acc.subscricoes || acc.subscricoes.length === 0) && <div className="text-slate-400 text-sm italic">Sem subscricoes registadas.</div>}
            <div className="space-y-2">
              {(acc.subscricoes || []).map((s, i) => (
                <div key={s.id || i} className="border border-tint rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`sub-${s.id || i}`}>
                  <div>
                    <div className="font-semibold text-navy">{s.plano_nome || s.plataforma_nome || s.nome || "-"}</div>
                    <div className="text-xs text-muted2">{s.periodo || "-"} - {eur(s.mrr || s.preco || 0)}/{s.periodo === "anual" ? "ano" : "mes"}{s.proxima_faturacao ? ` - proxima ${dt(s.proxima_faturacao)}` : ""}</div>
                  </div>
                  <Badge value={s.estado || "ativa"} />
                </div>
              ))}
            </div>
          </SectionBox>
        </div>
      )}

      {tab === "contactos" && (
        <div data-testid="tab-content-contactos">
          <div className="flex justify-end mb-3"><Btn onClick={() => setShowContact(true)} testid="add-contact-btn"><Plus size={16} /> Adicionar contacto</Btn></div>
          <div className="grid gap-2 md:grid-cols-2">
            {(acc.contactos || []).length === 0 && <div className="text-muted2 text-sm">Sem contactos.</div>}
            {(acc.contactos || []).map((c) => {
              const primaryPhone = c.telemovel || c.telefone || (c.telefones || [])[0];
              const email = c.email || (c.emails || [])[0];
              return (
                <div key={c.id}
                  data-contact-row-id={c.id}
                  className={`border border-tint rounded-lg p-3 bg-white transition-all ${highlightContactId === c.id ? "ring-2 ring-blue shadow-lg border-blue" : ""}`}
                  data-testid={`contact-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-navy truncate flex items-center gap-2">
                        {c.nome}
                        {c.principal && <span className="text-[10px] uppercase tracking-wider bg-blue/10 text-blue border border-blue/30 rounded px-1.5 py-0.5">principal</span>}
                      </div>
                      <div className="text-[11px] text-muted2">{[c.cargo, c.departamento].filter(Boolean).join(" - ") || "-"}</div>
                    </div>
                    <Badge value={c.ciclo_vida || "lead"} />
                  </div>
                  <div className="mt-2 space-y-0.5 text-[12px] text-navy">
                    {email && <div className="truncate">Email: <a href={`mailto:${email}`} className="text-blue hover:underline">{email}</a></div>}
                    {c.telefone && <div>Tel: <a href={`tel:${c.telefone}`} className="text-navy hover:text-blue">{c.telefone}</a></div>}
                    {c.telemovel && <div>Telem: <a href={`tel:${c.telemovel}`} className="text-navy hover:text-blue">{c.telemovel}</a></div>}
                    {!email && !c.telefone && !c.telemovel && (c.emails || []).length === 0 && (c.telefones || []).length === 0 && <div className="text-muted2 italic">sem contactos</div>}
                  </div>
                  {c.notas && <div className="text-[11px] text-slate-600 mt-2 whitespace-pre-wrap border-t border-tint pt-2">{c.notas}</div>}
                  {c.criado_em && (
                    <div className="mt-2 text-[10px] text-muted2" data-testid={`contact-criado-${c.id}`}>
                      Criado em {dt(c.criado_em)}
                      {(c.criado_por_nome || c.criado_por_email) && <> por <b className="text-navy">{c.criado_por_nome || c.criado_por_email}</b></>}
                    </div>
                  )}
                  {c.ultima_interacao && c.ultima_interacao.tipo && (
                    <div className="mt-2 border-t border-tint pt-2 text-[11px]" data-testid={`contact-ultima-${c.id}`}>
                      <div className="text-muted2 mb-1">Ultima interacao ({c.ultima_interacao.tipo}){c.ultima_interacao.em ? ` - ${dt(c.ultima_interacao.em)}` : ""}:</div>
                      {c.ultima_interacao.assunto && <div className="font-semibold text-navy truncate" title={c.ultima_interacao.assunto}>{c.ultima_interacao.assunto}</div>}
                      {c.ultima_interacao.estado && (
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          {c.ultima_interacao.estado.split(" - ").map((s, idx) => {
                            const cls = s === "enviado" ? "bg-slate-200 text-slate-700"
                              : s === "entregue" ? "bg-sky-100 text-sky-800"
                              : s === "aberto" ? "bg-emerald-100 text-emerald-800"
                              : s === "clicado" ? "bg-blue-100 text-blue-800"
                              : s === "devolvido" || s === "descartado" || s === "spam" ? "bg-red-100 text-red-800"
                              : s === "cancelou" ? "bg-amber-100 text-amber-800"
                              : "bg-slate-100 text-slate-600";
                            return (
                              <span key={idx} className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${cls}`}>
                                {s.toUpperCase()}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`consent-badges-${c.id}`}>
                    <span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 border ${c.politica_aceite ? "bg-green-50 text-green-700 border-green-300" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                      Politica {c.politica_aceite ? "OK" : "-"}
                    </span>
                    <span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 border ${c.marketing_optin ? "bg-blue/10 text-blue border-blue/30" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                      Marketing {c.marketing_optin ? "opt-in" : "opt-out"}
                    </span>
                    {(c.consent_history && c.consent_history.length > 0) && (
                      <button
                        onClick={() => alert("Evidencias RGPD:\n\n" + JSON.stringify(c.consent_history, null, 2))}
                        className="text-[10px] text-blue hover:underline"
                        data-testid={`consent-evidence-${c.id}`}
                        title="Ver evidencias de consentimento"
                      >
                        Ver evidencia ({c.consent_history.length})
                      </button>
                    )}
                  </div>
                  {primaryPhone && (
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <LigarButton number={primaryPhone} label="Ligar" />
                      {/^(\+3519|9)\d/.test(primaryPhone.replace(/[\s\-().]/g, "")) && (
                        <button data-testid={`sms-contact-${c.id}`} onClick={() => { setSmsContactos([c]); setShowSms(true); }}
                          className="inline-flex items-center gap-1 text-navy hover:bg-tint rounded px-2 py-1 text-xs font-semibold" title="Enviar SMS a este contacto">
                          <MessageSquare size={13} /> SMS
                        </button>
                      )}
                      <button data-testid={`push-campanha-${c.id}`} onClick={async () => {
                        try {
                          const { data } = await api.post("/channels/letscall/push-contact", { number: primaryPhone, name: c.nome, email: email || "" });
                          toast[data.ok ? "success" : "message"](data.ok ? "Contacto enviado para a campanha Let's Call." : `Nao enviado: ${data.erro}`);
                        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                      }} className="inline-flex items-center gap-1 text-navy hover:bg-tint rounded px-2 py-1 text-xs font-semibold">
                        <Phone size={13} /> Campanha
                      </button>
                    </div>
                  )}
                  <div className="mt-2 pt-2 border-t border-tint flex items-center gap-1 flex-wrap">
                    <button data-testid={`view-contact-${c.id}`} onClick={() => setDrawerContact(c)}
                      className="inline-flex items-center gap-1 text-navy hover:bg-tint rounded px-2 py-1 text-xs font-semibold" title="Ver detalhes">
                      <ExternalLink size={12} /> Ver
                    </button>
                    <button data-testid={`edit-contact-${c.id}`} onClick={() => openEditContact(c)}
                      className="inline-flex items-center gap-1 text-navy hover:bg-tint rounded px-2 py-1 text-xs font-semibold" title="Editar contacto">
                      <Pencil size={12} /> Editar
                    </button>
                    {email && (
                      <button data-testid={`email-contact-${c.id}`} onClick={() => openCompose(email, "", "")}
                        className="inline-flex items-center gap-1 text-navy hover:bg-tint rounded px-2 py-1 text-xs font-semibold" title="Enviar email (usa o remetente CRM configurado)">
                        <Mail size={12} /> Enviar email
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "agendamentos" && (
        <div data-testid="tab-content-agendamentos" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-xs text-muted2">Todas as reunioes/agendamentos ligados a esta conta (pedidos do site, marcacoes internas e sincronizados com M365).</div>
            <Btn onClick={() => openMeetingForm()} testid="new-meeting-btn"><Plus size={14} /> Novo agendamento</Btn>
          </div>

          {(() => {
            const pedidos = (acc.reunioes || []).filter((r) => (r.origem || "").includes("formulario") || r.estado === "provisorio");
            const agendadas = (acc.reunioes || []).filter((r) => !((r.origem || "").includes("formulario") || r.estado === "provisorio"));
            return (
              <>
                {pedidos.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold text-navy mb-2">Pedidos por confirmar ({pedidos.length})</h3>
                    <div className="space-y-2">
                      {pedidos.map((r) => <MeetingCard key={r.id} r={r} variant="pending" />)}
                    </div>
                  </div>
                )}
                <div>
                  <h3 className="text-sm font-bold text-navy mb-2 mt-2">Reunioes confirmadas ({agendadas.length})</h3>
                  {agendadas.length === 0 && <div className="text-muted2 text-sm">Sem reunioes confirmadas.</div>}
                  <div className="space-y-2">
                    {agendadas.map((r) => <MeetingCard key={r.id} r={r} variant="confirmed" />)}
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {tab === "chats" && (
        <div data-testid="tab-content-chats" className="space-y-4">
          <h3 className="text-sm font-bold text-navy mb-2">Conversas do Chat AI ({(acc.chats || []).length})</h3>
          {(acc.chats || []).length === 0 && <div className="text-muted2 text-sm">Sem conversas registadas.</div>}
          <div className="space-y-2">
            {(acc.chats || []).map((c) => (
              <div key={c.id} className="border border-tint rounded-lg p-3 bg-white" data-testid={`chat-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-navy">
                      Chat #{(c.id || "").slice(0, 8)}
                      {c.plataforma && <span className="ml-2 text-[10px] uppercase tracking-wider bg-blue/10 text-blue border border-blue/30 rounded px-1.5 py-0.5">{c.plataforma}</span>}
                    </div>
                    <div className="text-[11px] text-muted2 mt-0.5">{c.assunto || "-"} - {dt(c.criada_em)}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 border ${c.estado === "fechada" ? "bg-slate-50 text-slate-500 border-slate-200" : c.estado === "escalada" ? "bg-amber-50 text-amber-700 border-amber-300" : "bg-green-50 text-green-700 border-green-300"}`}>
                    {c.estado || "aberta"}
                  </span>
                </div>
                <div className="text-[11px] text-muted2 mt-1">{c.email || "sem email"} - {c.telefone || "sem telefone"}</div>
                {c.resumo && (
                  <div className="mt-2 text-[11px] text-slate-700 bg-blue/5 border border-blue/20 rounded p-2" data-testid={`chat-resumo-${c.id}`}>
                    <span className="font-semibold text-blue">Resumo IA: </span>{c.resumo}
                  </div>
                )}
                <button
                  onClick={async () => {
                    try {
                      const { data } = await api.get(`/chat-ai/sessions/${c.id}`);
                      alert("Transcript:\n\n" + (data.messages || []).map((m) => `[${(m.em || "").slice(0, 19)}] ${m.de}: ${m.texto}`).join("\n"));
                    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                  }}
                  className="mt-2 text-xs text-blue hover:underline"
                  data-testid={`chat-transcript-${c.id}`}
                >
                  Ver transcript
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "oportunidades" && (
        <div data-testid="tab-content-oportunidades">
          <div className="flex justify-end mb-3"><Btn onClick={() => setShowDeal(true)} testid="add-deal-btn"><Plus size={16} /> Nova oportunidade</Btn></div>
          <div className="space-y-2">
            {(acc.oportunidades || []).length === 0 && <div className="text-muted2 text-sm">Sem oportunidades.</div>}
            {(acc.oportunidades || []).map((d) => (
              <div key={d.id} className="border border-tint rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`deal-${d.id}`}>
                <div>
                  <div className="font-semibold text-navy">{d.nome}</div>
                  <div className="text-xs text-muted2">{d.plataforma || "-"} - {eur(d.mrr)}/mes - ARR {eur(d.arr)}</div>
                </div>
                <select
                  value={d.estagio}
                  onChange={(e) => changeStage(d.id, e.target.value)}
                  data-testid={`deal-stage-${d.id}`}
                  className="px-3 py-1.5 border border-tint rounded-lg text-sm bg-white"
                >
                  {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "mensagens" && (
        <div data-testid="tab-content-mensagens">
          {/* Filtros */}
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div>
              <label className="block text-xs text-muted2 mb-1 font-semibold">Tipo</label>
              <select value={mensagensFiltroTipo}
                onChange={(e) => { const v = e.target.value; setMensagensFiltroTipo(v); loadMensagens(v, mensagensFiltroEstado); }}
                data-testid="mensagens-filtro-tipo"
                className="px-2.5 py-1.5 border border-tint rounded-lg text-sm bg-white min-w-[130px]">
                <option value="">Todos os tipos</option>
                <option value="sms">SMS</option>
                <option value="whatsapp">WhatsApp</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-muted2 mb-1 font-semibold">Estado</label>
              <select value={mensagensFiltroEstado}
                onChange={(e) => { const v = e.target.value; setMensagensFiltroEstado(v); loadMensagens(mensagensFiltroTipo, v); }}
                data-testid="mensagens-filtro-estado"
                className="px-2.5 py-1.5 border border-tint rounded-lg text-sm bg-white min-w-[150px]">
                <option value="">Todos os estados</option>
                <option value="pendente">Pendente</option>
                <option value="enviado">Enviado</option>
                <option value="falhado">Falhado</option>
                <option value="cancelado">Cancelado</option>
              </select>
            </div>
            <div className="ml-auto text-xs text-muted2 self-end pb-1.5" data-testid="mensagens-total-label">
              {mensagensTotal} mensagem{mensagensTotal !== 1 ? "s" : ""}
            </div>
          </div>

          {mensagensLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((n) => (
                <div key={n} className="h-14 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : mensagens.length === 0 ? (
            <div className="text-sm text-muted2 py-8 text-center border border-tint rounded-lg bg-white" data-testid="mensagens-empty">
              Sem mensagens agendadas{mensagensFiltroTipo || mensagensFiltroEstado ? " para os filtros selecionados" : " para esta conta"}.
            </div>
          ) : (
            <div className="space-y-2">
              {mensagens.map((m, i) => {
                const isSms = m.tipo === "sms";
                const estadoCor = {
                  pendente: "bg-amber-50 text-amber-700 border-amber-200",
                  enviado: "bg-green-50 text-green-700 border-green-200",
                  falhado: "bg-red-50 text-red-600 border-red-200",
                  cancelado: "bg-slate-50 text-slate-500 border-slate-200",
                }[m.estado] || "bg-slate-50 text-slate-500 border-slate-200";
                const dt2 = (s) => s ? new Date(s).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : "-";
                return (
                  <div key={m.id || i} className="flex items-start gap-3 border border-tint rounded-lg p-3 bg-white"
                    data-testid={`mensagem-item-${i}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isSms ? "bg-blue/10" : "bg-[#25d366]/10"}`}>
                      {isSms
                        ? <MessageSquare size={15} className="text-blue" />
                        : <MessageCircle size={15} className="text-[#25d366]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-navy">{m.numero}</span>
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${estadoCor}`}
                          data-testid={`mensagem-estado-${i}`}>{m.estado}</span>
                        <span className="text-[10px] text-slate-500 uppercase font-semibold">{m.tipo}</span>
                      </div>
                      <div className="text-sm text-slate-700 mt-0.5 truncate">
                        {m.template_name ? <span className="italic">Template: {m.template_name}</span> : m.mensagem}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-400">
                        <span>Agendado: {dt2(m.agendado_para)}</span>
                        {m.enviado_em && <span>Enviado: {dt2(m.enviado_em)}</span>}
                        {m.erro && <span className="text-red-500 truncate max-w-[200px]" title={m.erro}>Erro: {m.erro}</span>}
                      </div>
                    </div>
                    {m.estado === "pendente" && (
                      <button
                        onClick={async () => {
                          if (!confirm("Cancelar esta mensagem agendada?")) return;
                          try {
                            await api.delete(`/scheduled-messages/${m.id}`);
                            toast.success("Mensagem cancelada.");
                            loadMensagens();
                          } catch (e) { toast.error(e.response?.data?.detail || "Erro ao cancelar."); }
                        }}
                        data-testid={`mensagem-cancelar-${i}`}
                        className="shrink-0 text-[11px] px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50 transition">
                        Cancelar
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === "emails" && (
        <div data-testid="tab-content-emails">
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm text-muted2">Emails enviados e recebidos com este cliente, com rastreabilidade do SendGrid.</p>
            <Btn onClick={() => openCompose()} testid="compose-email-btn"><Send size={15} /> Escrever email</Btn>
          </div>
          {followups.filter((f) => f.estado === "pendente").length > 0 && (
            <div className="mb-4 border border-amber-200 bg-amber-50 rounded-lg p-3" data-testid="followups-pendentes">
              <div className="text-sm font-semibold text-amber-800 mb-2">Follow-ups agendados</div>
              <div className="space-y-1">
                {followups.filter((f) => f.estado === "pendente").map((f) => (
                  <div key={f.id} className="flex items-center justify-between text-sm" data-testid={`followup-${f.id}`}>
                    <span className="text-navy">{f.assunto || "(sem assunto)"} - <span className="text-muted2">para {f.para}, se sem resposta ate {dt(f.agendado_para)}</span></span>
                    <button onClick={() => cancelFollowup(f.id)} data-testid={`followup-cancel-${f.id}`} className="text-red-600 text-xs font-semibold hover:underline">Cancelar</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-2">
            {emails.length === 0 && <div className="text-muted2 text-sm">Sem emails registados para este cliente.</div>}
            {emails.map((e, i) => {
              const enviado = (e.direcao || "enviado") === "enviado";
              const tr = e.tracking || {};
              let est = e.estado, cor = "bg-tint text-navy";
              if (!enviado) { est = "recebido"; cor = "bg-blue/10 text-blue"; }
              else if (tr.falhou) { est = "devolvido"; cor = "bg-red-50 text-red-600"; }
              else if (tr.clicado) { est = "clicado"; cor = "bg-green-100 text-green-700"; }
              else if (tr.aberto) { est = "aberto"; cor = "bg-green-50 text-green-700"; }
              else if (tr.entregue) { est = "entregue"; cor = "bg-emerald-50 text-emerald-700"; }
              else if (e.estado === "falhado") { cor = "bg-red-50 text-red-600"; }
              else if (e.estado === "nao_configurado") { est = "nao enviado"; cor = "bg-amber-50 text-amber-700"; }
              return (
                <div key={i} className="border border-tint rounded-lg p-3" data-testid={`email-item-${i}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 font-semibold text-navy text-sm">
                      {enviado ? <ArrowUpRight size={14} className="text-blue" /> : <ArrowDownLeft size={14} className="text-green-600" />}
                      {e.assunto || "(sem assunto)"}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${cor}`} data-testid={`email-status-${i}`}>{est}</span>
                      <span className="text-xs text-muted2">{dt(e.em)}</span>
                    </div>
                  </div>
                  <div className="text-xs text-muted2 mt-1">{enviado ? `Para ${e.para || "-"}` : `De ${e.de || "-"}`}</div>
                  {e.corpo && (
                    <>
                      <div className="text-sm text-navy mt-1 line-clamp-3">{e.corpo.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()}</div>
                      <button onClick={() => setEmailPreview(e)} data-testid={`email-view-content-${i}`}
                        className="mt-1.5 inline-flex items-center gap-1 text-blue text-xs font-semibold hover:underline">
                        <Eye size={12} /> Ver conteudo completo
                      </button>
                    </>
                  )}
                  {(e.anexos || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(e.anexos || []).map((a, k) => <span key={k} className="inline-flex items-center gap-1 bg-tint text-navy rounded px-2 py-0.5 text-[11px]"><Paperclip size={11} /> {a.filename}</span>)}
                    </div>
                  )}
                  {e.erro && <div className="text-xs text-red-600 mt-1">{e.erro}</div>}
                  {!enviado && <button onClick={() => replyEmail(e)} data-testid={`email-reply-${i}`} className="mt-2 inline-flex items-center gap-1 text-blue text-sm font-semibold hover:underline"><Reply size={13} /> Responder</button>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "marketing" && <AccountMarketingTab accountId={id} />}

      {tab === "timeline" && (
        <div data-testid="tab-content-timeline">
          {/* Barra de accoes + filtros */}
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            {/* Filtros rapidos */}
            <div className="flex items-center gap-1 flex-wrap" data-testid="timeline-filters">
              {TIMELINE_FILTERS.map((f) => {
                const count = f.key ? timeline.filter((t) => t.tipo === f.key).length : timeline.length;
                const active = timelineFiltro === f.key;
                return (
                  <button key={String(f.key)} onClick={() => setTimelineFiltro(f.key)}
                    data-testid={`timeline-filter-${f.key || "todos"}`}
                    className={`text-xs px-2.5 py-1 rounded-full border transition font-medium ${
                      active ? "bg-navy text-white border-navy" : "bg-white text-navy border-tint hover:border-navy/40"
                    }`}>
                    {f.label}
                    {count > 0 && <span className={`ml-1 text-[9px] ${active ? "opacity-70" : "text-muted2"}`}>{count}</span>}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2">
              <Btn variant="ghost" onClick={() => { setActivity({ tipo: "nota", assunto: "", corpo: "", contact_id: "", contact_ids: [] }); setShowActivity(true); }} testid="add-note-btn">
                <StickyNote size={16} /> Nova nota
              </Btn>
              <Btn variant="ghost" onClick={() => setShowActivity(true)} testid="add-activity-btn">
                <Plus size={16} /> Registar atividade
              </Btn>
            </div>
          </div>

          <div className="relative pl-6 border-l-2 border-tint space-y-4">
            {(() => {
              const filtered = timelineFiltro ? timeline.filter((t) => t.tipo === timelineFiltro) : timeline;
              if (filtered.length === 0) return <div className="text-muted2 text-sm" data-testid="timeline-empty">Sem atividade{timelineFiltro ? " deste tipo" : ""} registada.</div>;
              return filtered.map((it, i) => {
                const Icon = TIPO_ICON[it.tipo] || StickyNote;
                const callMeta = CALL_RESULT_META[it.resultado];
                const isChamada = it.tipo === "atividade:chamada";
                const meta = it.meta || {};
                const durSec = meta.duration_seconds;
                const durStr = durSec != null ? (durSec >= 60 ? `${Math.floor(durSec / 60)}m${durSec % 60}s` : `${durSec}s`) : null;
                const extNum = meta.direction === "outbound" ? meta.callee_number : meta.caller_number;
                const DirecaoIcon = meta.direction === "outbound" ? PhoneOutgoing : (meta.direction === "inbound" ? PhoneIncoming : null);
                const callId = meta.letscall_call_id;
                const actId = it.id || String(i);
                const transcState = expandedTranscriptions[actId];
                return (
                  <div key={i} className="relative" data-testid={`timeline-item-${i}`}>
                    {callMeta ? (
                      <div className={`absolute -left-[33px] w-7 h-7 rounded-full ${callMeta.dot} ring-2 ${callMeta.ring} flex items-center justify-center shadow-md`}
                        title={callMeta.label} data-testid={`timeline-call-result-${i}`}>
                        <callMeta.icon size={15} className="text-white" />
                      </div>
                    ) : (
                      <div className="absolute -left-[31px] w-5 h-5 rounded-full bg-blue flex items-center justify-center"><Icon size={11} className="text-white" /></div>
                    )}
                    <div className="border border-tint rounded-lg p-3 bg-white">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="font-semibold text-navy text-sm">{it.titulo}</span>
                        <span className="text-xs text-muted2">{dt(it.em)}</span>
                      </div>
                      {/* Detalhes de chamada LetsCall One */}
                      {isChamada && (extNum || durStr || callMeta || DirecaoIcon) && (
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid={`timeline-call-info-${i}`}>
                          {callMeta && (
                            <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${callMeta.badge}`}>
                              <callMeta.icon size={9} /> {callMeta.label}
                            </span>
                          )}
                          {DirecaoIcon && <DirecaoIcon size={12} className="text-slate-400" title={meta.direction === "outbound" ? "Efetuada" : "Recebida"} />}
                          {extNum && <span className="text-[11px] font-mono text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">{extNum}</span>}
                          {durStr && <span className="text-[11px] text-slate-500 bg-tint rounded px-1.5 py-0.5">{durStr}</span>}
                          {meta.agent_extension && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5"
                              title="Extensao do agente" data-testid={`timeline-extension-${i}`}>
                              <Headset size={10} /> Ext. {meta.agent_extension}
                            </span>
                          )}
                          {meta.agent_nome && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5"
                              title="Agente" data-testid={`timeline-agente-${i}`}>
                              <User size={10} /> {meta.agent_nome}
                            </span>
                          )}
                        </div>
                      )}
                      {/* Resto dos badges / detalhe para nao-chamadas */}
                      {!isChamada && callMeta && (
                        <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border mt-1 ${callMeta.badge}`} data-testid={`timeline-call-badge-${i}`}>
                          {callMeta.label}
                        </span>
                      )}
                      {it.estado_email && (
                        <div className="mt-1 flex flex-wrap items-center gap-1" data-testid={`timeline-email-estado-${i}`}>
                          {it.estado_email.split(" - ").map((s, idx) => {
                            const cls = s === "enviado" ? "bg-slate-200 text-slate-700"
                              : s === "entregue" ? "bg-sky-100 text-sky-800"
                              : s === "aberto" ? "bg-emerald-100 text-emerald-800"
                              : s === "clicado" ? "bg-blue-100 text-blue-800"
                              : s === "devolvido" || s === "descartado" || s === "spam" ? "bg-red-100 text-red-800"
                              : s === "cancelou" ? "bg-amber-100 text-amber-800"
                              : "bg-slate-100 text-slate-600";
                            return (
                              <span key={idx} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${cls}`}>
                                {s.toUpperCase()}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      <div className="text-xs text-muted2 mt-0.5">{it.tipo.replace(/[:_]/g, " ")}</div>
                      {(it.contactos && it.contactos.length > 0) && (
                        <div className="mt-1.5 flex flex-wrap items-center gap-1" data-testid={`timeline-contacts-${i}`}>
                          {it.contactos.map((c) => (
                            <button key={c.id} type="button" onClick={() => nav(`/app/crm/contas/${id}?tab=contactos&contact_id=${c.id}`)}
                              data-testid={`timeline-contact-${c.id}-${i}`}
                              title={c.email || c.nome}
                              className="inline-flex items-center gap-1 text-[11px] bg-blue/5 hover:bg-blue/10 text-navy px-2 py-0.5 rounded-full border border-blue/20 transition">
                              <span className="w-4 h-4 rounded-full bg-blue text-white flex items-center justify-center text-[9px] font-bold">
                                {(c.nome || "?").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase() || "?"}
                              </span>
                              <span className="font-medium">{c.nome}</span>
                              {c.departamento && <span className="text-[9px] uppercase text-blue">{c.departamento}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                      {it.detalhe && <div className="text-sm mt-1">{it.detalhe}</div>}
                      {it.tipo === "fatura" && (
                        <div className="mt-2 space-y-1.5" data-testid={`timeline-fatura-${i}`}>
                          {it.mb && it.mb.entidade && (
                            <div className="text-xs bg-tint rounded p-2 inline-flex gap-3" data-testid={`timeline-mb-${i}`}>
                              <span>Ent. <code className="font-semibold">{it.mb.entidade}</code></span>
                              <span>Ref. <code className="font-semibold">{it.mb.referencia}</code></span>
                              {it.mb.valor != null && <span className="text-muted2">{eur(it.mb.valor)}</span>}
                            </div>
                          )}
                          <div className="flex gap-3 text-xs">
                            {it.pdf_url && <a href={it.pdf_url} target="_blank" rel="noreferrer" data-testid={`timeline-pdf-${i}`} className="text-blue hover:underline font-semibold">Fatura PDF</a>}
                            {it.recibo_pdf_url && <a href={it.recibo_pdf_url} target="_blank" rel="noreferrer" data-testid={`timeline-recibo-${i}`} className="text-green-600 hover:underline font-semibold">Recibo PDF</a>}
                            {it.recibo_id && !it.recibo_pdf_url && <span className="text-green-600">Recibo emitido</span>}
                          </div>
                        </div>
                      )}
                      {/* Botao de transcricao para chamadas LetsCall One */}
                      {isChamada && callId && (
                        <div className="mt-2 pt-2 border-t border-tint">
                          {transcState === undefined || transcState === null ? (
                            <button
                              onClick={async () => {
                                setExpandedTranscriptions((p) => ({ ...p, [actId]: "loading" }));
                                try {
                                  const { data } = await api.get(`/channels/letscall/transcription/${callId}`);
                                  setExpandedTranscriptions((p) => ({ ...p, [actId]: data.transcription || "" }));
                                } catch {
                                  setExpandedTranscriptions((p) => ({ ...p, [actId]: "" }));
                                  toast.error("Transcricao nao disponivel.");
                                }
                              }}
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue hover:text-navy"
                              data-testid={`timeline-transcription-btn-${i}`}>
                              <FileAudio size={12} /> Ver transcricao
                            </button>
                          ) : transcState === "loading" ? (
                            <span className="text-xs text-muted2 animate-pulse">A carregar...</span>
                          ) : (
                            <div>
                              <button
                                onClick={() => setExpandedTranscriptions((p) => ({ ...p, [actId]: null }))}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue hover:text-navy"
                                data-testid={`timeline-transcription-toggle-${i}`}>
                                <FileAudio size={12} /> Ocultar transcricao <ChevronUp size={11} />
                              </button>
                              <div className="mt-1.5 bg-slate-50 border border-slate-200 rounded p-2 text-[11px] text-navy max-h-36 overflow-y-auto whitespace-pre-wrap"
                                data-testid={`timeline-transcription-text-${i}`}>
                                {transcState
                                  ? (typeof transcState === "string" ? transcState : transcState.text || transcState.transcript || JSON.stringify(transcState, null, 2))
                                  : <span className="italic text-muted2">Transcricao nao disponivel para esta chamada.</span>}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {tab === "chamadas" && (
        <div data-testid="tab-content-chamadas">
          {/* Filtros */}
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div>
              <label className="block text-xs text-muted2 mb-1 font-semibold">Resultado</label>
              <select
                value={chamadasFiltroResultado}
                onChange={(e) => {
                  const v = e.target.value;
                  setChamadasFiltroResultado(v);
                  loadChamadas(1, v, chamadasDataInicio, chamadasDataFim);
                }}
                data-testid="chamadas-filtro-resultado"
                className="px-2.5 py-1.5 border border-tint rounded-lg text-sm bg-white min-w-[160px]">
                <option value="">Todos os resultados</option>
                <option value="atendeu">Atendida</option>
                <option value="nao_atendeu">Nao atendeu</option>
                <option value="ocupado">Linha ocupada</option>
                <option value="caixa_voz">Caixa de voz</option>
                <option value="contactar_mais_tarde">Contactar mais tarde</option>
                <option value="pessoa_indisponivel">Pessoa indisponivel</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-muted2 mb-1 font-semibold">Data inicio</label>
              <input type="date" value={chamadasDataInicio}
                onChange={(e) => {
                  const v = e.target.value;
                  setChamadasDataInicio(v);
                  loadChamadas(1, chamadasFiltroResultado, v, chamadasDataFim);
                }}
                data-testid="chamadas-filtro-data-inicio"
                className="px-2.5 py-1.5 border border-tint rounded-lg text-sm bg-white" />
            </div>
            <div>
              <label className="block text-xs text-muted2 mb-1 font-semibold">Data fim</label>
              <input type="date" value={chamadasDataFim}
                onChange={(e) => {
                  const v = e.target.value;
                  setChamadasDataFim(v);
                  loadChamadas(1, chamadasFiltroResultado, chamadasDataInicio, v);
                }}
                data-testid="chamadas-filtro-data-fim"
                className="px-2.5 py-1.5 border border-tint rounded-lg text-sm bg-white" />
            </div>
            {(chamadasFiltroResultado || chamadasDataInicio || chamadasDataFim) && (
              <button
                onClick={() => {
                  setChamadasFiltroResultado("");
                  setChamadasDataInicio("");
                  setChamadasDataFim("");
                  loadChamadas(1, "", "", "");
                }}
                data-testid="chamadas-filtros-limpar"
                className="px-3 py-1.5 text-xs text-muted2 border border-tint rounded-lg hover:bg-tint transition">
                Limpar filtros
              </button>
            )}
            <div className="ml-auto text-xs text-muted2 self-end pb-1.5" data-testid="chamadas-total-label">
              {chamadasTotal} chamada{chamadasTotal !== 1 ? "s" : ""}
            </div>
          </div>

          {/* Lista de chamadas */}
          {chamadasLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((n) => (
                <div key={n} className="border border-tint rounded-lg p-3 bg-white animate-pulse">
                  <div className="h-4 bg-slate-200 rounded w-1/3 mb-2" />
                  <div className="h-3 bg-slate-100 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : chamadas.length === 0 ? (
            <div className="text-sm text-muted2 py-8 text-center border border-tint rounded-lg bg-white" data-testid="chamadas-empty">
              Sem chamadas registadas{(chamadasFiltroResultado || chamadasDataInicio || chamadasDataFim) ? " para os filtros selecionados" : " para esta conta"}.
            </div>
          ) : (
            <div className="relative pl-6 border-l-2 border-tint space-y-4">
              {chamadas.map((c, i) => {
                const callMeta = CALL_RESULT_META[c.resultado];
                const meta = c.meta || {};
                const durSec = meta.duration_seconds;
                const durStr = durSec != null ? (durSec >= 60 ? `${Math.floor(durSec / 60)}m${durSec % 60}s` : `${durSec}s`) : null;
                const extNum = meta.direction === "outbound" ? meta.callee_number : meta.caller_number;
                const DirecaoIcon = meta.direction === "outbound" ? PhoneOutgoing : (meta.direction === "inbound" ? PhoneIncoming : null);
                const callId = meta.letscall_call_id;
                const actId = c.id || String(i);
                const transcState = chamadasTranscriptions[actId];
                return (
                  <div key={actId} className="relative" data-testid={`chamada-item-${i}`}>
                    {callMeta ? (
                      <div className={`absolute -left-[33px] w-7 h-7 rounded-full ${callMeta.dot} ring-2 ${callMeta.ring} flex items-center justify-center shadow-md`}
                        title={callMeta.label}>
                        <callMeta.icon size={15} className="text-white" />
                      </div>
                    ) : (
                      <div className="absolute -left-[31px] w-5 h-5 rounded-full bg-blue flex items-center justify-center">
                        <Phone size={11} className="text-white" />
                      </div>
                    )}
                    <div className="border border-tint rounded-lg p-3 bg-white">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="font-semibold text-navy text-sm">{c.assunto || c.titulo || "Chamada"}</span>
                        <span className="text-xs text-muted2">{dt(c.em)}</span>
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid={`chamada-meta-${i}`}>
                        {callMeta && (
                          <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${callMeta.badge}`}
                            data-testid={`chamada-resultado-badge-${i}`}>
                            <callMeta.icon size={9} /> {callMeta.label}
                          </span>
                        )}
                        {DirecaoIcon && (
                          <span className="inline-flex items-center gap-0.5 text-[11px] text-slate-500 bg-tint rounded px-1.5 py-0.5"
                            data-testid={`chamada-direcao-${i}`}>
                            <DirecaoIcon size={11} /> {meta.direction === "outbound" ? "Efetuada" : "Recebida"}
                          </span>
                        )}
                        {extNum && (
                          <span className="text-[11px] font-mono text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded"
                            data-testid={`chamada-numero-${i}`}>
                            {extNum}
                          </span>
                        )}
                        {durStr && (
                          <span className="text-[11px] text-slate-500 bg-tint rounded px-1.5 py-0.5"
                            data-testid={`chamada-duracao-${i}`}>
                            {durStr}
                          </span>
                        )}
                        {meta.agent_extension && (
                          <span className="inline-flex items-center gap-1 text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5"
                            title="Extensao do agente" data-testid={`chamada-extension-${i}`}>
                            <Headset size={10} /> Ext. {meta.agent_extension}
                          </span>
                        )}
                        {meta.agent_nome && (
                          <span className="inline-flex items-center gap-1 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5"
                            title="Agente que geriu a chamada" data-testid={`chamada-agente-${i}`}>
                            <User size={10} /> {meta.agent_nome}
                          </span>
                        )}
                      </div>
                      {c.corpo && <div className="text-xs text-muted2 mt-1">{c.corpo}</div>}
                      {/* Acoes rapidas: Ligar + WhatsApp */}
                      {extNum && (
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          <LigarButton number={extNum} label="Ligar" className="text-xs px-2 py-1 border border-tint rounded-md hover:bg-tint transition inline-flex items-center gap-1.5" />
                          <button
                            onClick={() => setChamadasWaOpen({ numero: extNum, assunto: c.assunto || "Chamada" })}
                            data-testid={`chamada-whatsapp-btn-${i}`}
                            className="inline-flex items-center gap-1.5 text-xs px-2 py-1 border border-[#25d366]/40 text-[#25d366] rounded-md hover:bg-[#25d366]/5 transition font-semibold">
                            <MessageCircle size={12} /> WhatsApp
                          </button>
                        </div>
                      )}
                      {/* Botao de transcricao */}
                      {callId && (
                        <div className="mt-2 pt-2 border-t border-tint">
                          {transcState === undefined || transcState === null ? (
                            <button
                              onClick={async () => {
                                setChamadasTranscriptions((p) => ({ ...p, [actId]: "loading" }));
                                try {
                                  const { data } = await api.get(`/channels/letscall/transcription/${callId}`);
                                  setChamadasTranscriptions((p) => ({ ...p, [actId]: data.transcription || "" }));
                                } catch {
                                  setChamadasTranscriptions((p) => ({ ...p, [actId]: "" }));
                                  toast.error("Transcricao nao disponivel.");
                                }
                              }}
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue hover:text-navy"
                              data-testid={`chamada-transcription-btn-${i}`}>
                              <FileAudio size={12} /> Ver transcricao
                            </button>
                          ) : transcState === "loading" ? (
                            <span className="text-xs text-muted2 animate-pulse">A carregar...</span>
                          ) : (
                            <div>
                              <button
                                onClick={() => setChamadasTranscriptions((p) => ({ ...p, [actId]: null }))}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue hover:text-navy"
                                data-testid={`chamada-transcription-toggle-${i}`}>
                                <FileAudio size={12} /> Ocultar transcricao <ChevronUp size={11} />
                              </button>
                              <div className="mt-1.5 bg-slate-50 border border-slate-200 rounded p-2 text-[11px] text-navy max-h-36 overflow-y-auto whitespace-pre-wrap"
                                data-testid={`chamada-transcription-text-${i}`}>
                                {transcState
                                  ? (typeof transcState === "string" ? transcState : transcState.text || transcState.transcript || JSON.stringify(transcState, null, 2))
                                  : <span className="italic text-muted2">Transcricao nao disponivel para esta chamada.</span>}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Paginacao */}
          {!chamadasLoading && chamadasTotalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-5" data-testid="chamadas-pagination">
              <button
                onClick={() => { const p = chamadasPage - 1; setChamadasPage(p); loadChamadas(p); }}
                disabled={chamadasPage <= 1}
                data-testid="chamadas-prev-btn"
                className="px-3 py-1.5 text-sm border border-tint rounded-lg disabled:opacity-40 hover:bg-tint transition">
                Anterior
              </button>
              <span className="text-sm text-muted2" data-testid="chamadas-page-label">
                Pagina {chamadasPage} de {chamadasTotalPages}
              </span>
              <button
                onClick={() => { const p = chamadasPage + 1; setChamadasPage(p); loadChamadas(p); }}
                disabled={chamadasPage >= chamadasTotalPages}
                data-testid="chamadas-next-btn"
                className="px-3 py-1.5 text-sm border border-tint rounded-lg disabled:opacity-40 hover:bg-tint transition">
                Seguinte
              </button>
            </div>
          )}
        </div>
      )}

      {tab === "portal" && (
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl" data-testid="tab-content-portal">
          <div className="border border-tint rounded-xl p-4 space-y-4">
            <h3 className="font-bold text-navy">Acesso ao portal do cliente</h3>
            <label className="flex items-center gap-2 text-sm text-navy">
              <input type="checkbox" checked={!!acc.permitir_cancelamento_recorrente} onChange={(e) => toggleCancelFlag(e.target.checked)} data-testid="portal-cancel-flag" />
              Permitir ao cliente cancelar a subscricao recorrente
            </label>
            <label className="flex items-center gap-2 text-sm text-navy">
              <input type="checkbox" checked={!!acc.isento_aprovacao} onChange={(e) => toggleExemptFlag(e.target.checked)} data-testid="account-exempt-flag" />
              Isentar esta conta de aprovacoes de venda
            </label>
            <div className="border-t border-tint pt-3 space-y-2">
              <div className="text-xs text-muted2 uppercase tracking-wide">Criar acesso</div>
              <Field label="Nome" value={newUser.nome} onChange={(v) => setNewUser({ ...newUser, nome: v })} testid="pu-nome" />
              <Field label="Email" value={newUser.email} onChange={(v) => setNewUser({ ...newUser, email: v })} testid="pu-email" />
              <label className="flex items-center gap-2 text-sm text-navy">
                <input type="checkbox" checked={newUser.enviar_convite} onChange={(e) => setNewUser({ ...newUser, enviar_convite: e.target.checked })} data-testid="pu-invite" />
                Enviar convite por email (o cliente define a password)
              </label>
              {!newUser.enviar_convite && (
                <Field label="Password inicial (min. 8)" type="password" value={newUser.password} onChange={(v) => setNewUser({ ...newUser, password: v })} testid="pu-password" />
              )}
              <Btn onClick={createPortalUser} testid="pu-create">Criar acesso</Btn>
            </div>
            <div className="border-t border-tint pt-3">
              <div className="text-xs text-muted2 uppercase tracking-wide mb-1">Utilizadores do portal</div>
              {portalUsers.length === 0 && <div className="text-sm text-muted2">Sem acessos criados.</div>}
              {portalUsers.map((u) => <div key={u.id} className="text-sm text-navy" data-testid={`pu-item-${u.id}`}>{u.email}</div>)}
            </div>
          </div>

          <div className="border border-tint rounded-xl p-4 space-y-3">
            <h3 className="font-bold text-navy">Nova venda (governacao)</h3>
            {acc.divida_aberta > 0 && (
              <div data-testid="sale-credit-warning" className={`text-xs rounded-lg p-2 ${acc.excede_credito ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
                Aviso: cliente com {eur(acc.divida_aberta)} em aberto{acc.excede_credito ? " (limite excedido)" : ""}. A venda podera exigir aprovacao.
              </div>
            )}
            <p className="text-xs text-muted2">Ativa se houver proposta aceite e contrato assinado. Caso contrario, o administrador pode fazer override com dados de compliance (fica pendente de assinatura de contrato).</p>
            <Field label="Item / plano" value={sale.nome} onChange={(v) => setSale({ ...sale, nome: v })} testid="sale-nome" />
            <Field label="Preco (EUR/mes)" type="number" value={sale.preco} onChange={(v) => setSale({ ...sale, preco: v })} testid="sale-preco" />
            <label className="flex items-center gap-2 text-sm text-navy">
              <input type="checkbox" checked={sale.override} onChange={(e) => setSale({ ...sale, override: e.target.checked })} data-testid="sale-override" />
              Override do administrador (compliance obrigatorio)
            </label>
            {sale.override && (
              <div className="space-y-2 bg-tint rounded-lg p-3">
                <Field label="NIF" value={sale.nif} onChange={(v) => setSale({ ...sale, nif: v })} testid="sale-nif" />
                <Field label="Morada de faturacao" value={sale.morada} onChange={(v) => setSale({ ...sale, morada: v })} testid="sale-morada" />
                <Field label="Email de faturacao" value={sale.email_faturacao} onChange={(v) => setSale({ ...sale, email_faturacao: v })} testid="sale-email" />
                <label className="flex items-center gap-2 text-sm text-navy">
                  <input type="checkbox" checked={sale.dpa_aceite} onChange={(e) => setSale({ ...sale, dpa_aceite: e.target.checked })} data-testid="sale-dpa" /> DPA aceite pelo cliente
                </label>
              </div>
            )}
            <Btn onClick={createSale} testid="sale-create">Lancar venda</Btn>
          </div>
        </div>
      )}

      <Modal open={showEmail} onClose={() => setShowEmail(false)} title="Escrever email" testid="email-modal" size="xl">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
      <div className="space-y-4">
        {emailContacts.length === 0 && (
          <div className="mb-2 rounded-md p-2.5 bg-amber-50 border border-amber-200 text-[12px] text-amber-800" data-testid="email-no-contacts">
            Esta conta ainda nao tem contactos com email. <button type="button" onClick={() => { setShowEmail(false); setTab("contactos"); setTimeout(() => setShowContact(true), 100); }} className="underline font-semibold">Adicionar contacto</button> para pre-preencher automaticamente.
          </div>
        )}
        {emailContacts.length > 0 && (
          <div>
            <label className="block text-sm font-semibold text-navy mb-1">Contactos disponiveis desta ficha</label>
            <select
              value=""
              onChange={(e) => {
                const chosen = emailContacts.find((c) => c.email === e.target.value);
                if (chosen) {
                  const existing = (emailForm.para || "").split(",").map((s) => s.trim()).filter(Boolean);
                  if (!existing.includes(chosen.email)) existing.push(chosen.email);
                  setEmailForm({ ...emailForm, para: existing.join(", ") });
                }
              }}
              data-testid="email-contact-picker"
              className="w-full px-3 py-2 border border-tint rounded-lg bg-white text-sm mb-2"
            >
              <option value="">+ Adicionar destinatario por departamento...</option>
              {(() => {
                // Agrupar por departamento
                const groups = {};
                emailContacts.forEach((c) => {
                  const key = c.departamento || "Sem departamento";
                  (groups[key] = groups[key] || []).push(c);
                });
                return Object.keys(groups).sort().map((dept) => (
                  <optgroup key={dept} label={dept}>
                    {groups[dept].map((c, i) => (
                      <option key={`${c.email}-${i}`} value={c.email}>
                        {(c.nome || c.email) + (c.cargo ? ` - ${c.cargo}` : "") + (c.principal ? " *" : "") + " <" + c.email + ">"}
                      </option>
                    ))}
                  </optgroup>
                ));
              })()}
            </select>
            <p className="text-[11px] text-muted2">O contacto principal (*) fica pre-selecionado no campo Para. Adicione mais destinatarios acima ou edite manualmente separando por virgula.</p>
          </div>
        )}
        <Field label="Para (multiplos emails separados por virgula)" value={emailForm.para} onChange={(v) => setEmailForm({ ...emailForm, para: v })} placeholder="email@cliente.pt" testid="email-para" />
        {templates.length > 0 && (
          <div>
            <label className="block text-sm font-semibold text-navy mb-1">Template (opcional)</label>
            <select value={selectedTemplate} onChange={(e) => e.target.value && applyTemplate(e.target.value)}
                    data-testid="email-template"
                    className="w-full px-3 py-2 border border-tint rounded-lg bg-white text-sm">
              <option value="">-- Escolher template --</option>
              {(() => {
                const crmT = templates.filter((t) => t.origem === "crm");
                const platT = templates.filter((t) => t.origem === "plataforma");
                const legacyT = templates.filter((t) => !t.origem);
                return (
                  <>
                    {crmT.length > 0 && (
                      <optgroup label="Templates CRM">
                        {crmT.map((t) => (
                          <option key={t.chave} value={t.chave}>{t.nome}{t.categoria && ` (${t.categoria})`}</option>
                        ))}
                      </optgroup>
                    )}
                    {platT.length > 0 && (
                      <optgroup label="Templates da plataforma (Definicoes -> Templates email)">
                        {platT.map((t) => (
                          <option key={t.chave} value={t.chave}>
                            {t.nome}{t.plataforma ? ` [${t.plataforma}]` : ""}{t.categoria && t.categoria !== "custom" ? ` (${t.categoria})` : ""}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {legacyT.length > 0 && legacyT.map((t) => (
                      <option key={t.chave} value={t.chave}>{t.nome}</option>
                    ))}
                  </>
                );
              })()}
            </select>
          </div>
        )}
        <Field label="Assunto" value={emailForm.assunto} onChange={(v) => setEmailForm({ ...emailForm, assunto: v })} testid="email-assunto" />
        <div>
          <label className="block text-sm font-semibold text-navy mb-1">Mensagem</label>
          <RichText value={emailForm.html} onChange={(v) => setEmailForm({ ...emailForm, html: v })} minHeight={220} testid="email-body-editor" />
        </div>
        <label className="flex items-center gap-2 text-sm text-navy">
          <input type="checkbox" checked={incluirAssinatura} onChange={(e) => setIncluirAssinatura(e.target.checked)} data-testid="email-assinatura-toggle" /> Incluir a minha assinatura
        </label>
        {proposals.length > 0 && (
          <div>
            <label className="block text-sm font-semibold text-navy mb-1">Anexar proposta / contrato</label>
            <div className="space-y-1 max-h-28 overflow-y-auto">
              {proposals.map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-sm text-navy" data-testid={`email-proposta-${p.id}`}>
                  <input type="checkbox" checked={propostaIds.includes(p.id)} onChange={(e) => setPropostaIds(e.target.checked ? [...propostaIds, p.id] : propostaIds.filter((x) => x !== p.id))} />
                  {p.numero || p.id} - {p.titulo || "Proposta"} ({(p.total || 0).toFixed ? (p.total || 0).toFixed(2) : p.total} EUR)
                </label>
              ))}
            </div>
          </div>
        )}
        <div>
          <label className="block text-sm font-semibold text-navy mb-1">Anexos</label>
          <label className="inline-flex items-center gap-1 text-blue text-sm font-semibold cursor-pointer hover:underline" data-testid="email-anexo-label">
            <Paperclip size={14} /> Adicionar ficheiro
            <input type="file" multiple className="hidden" data-testid="email-anexo-input" onChange={(e) => onAnexos(e.target.files)} />
          </label>
          <div className="flex flex-wrap gap-2 mt-2">
            {anexos.map((a, k) => (
              <span key={k} className="inline-flex items-center gap-1 bg-tint text-navy rounded px-2 py-1 text-xs" data-testid={`email-anexo-${k}`}>
                <Paperclip size={12} /> {a.filename}
                <button onClick={() => setAnexos(anexos.filter((_, idx) => idx !== k))} className="text-red-600"><X size={12} /></button>
              </span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-navy">Follow-up automatico se sem resposta em</label>
          <input type="number" min="0" value={followupDias} onChange={(e) => setFollowupDias(e.target.value)} data-testid="email-followup-dias" className="w-16 px-2 py-1 border border-tint rounded text-sm" />
          <span className="text-sm text-muted2">dias (0 = desligado)</span>
        </div>
      </div>
      <div className="h-full flex flex-col">
        <label className="block text-sm font-semibold text-navy mb-1">Pre-visualizacao (como sera enviado)</label>
        <div className="border border-tint rounded-lg overflow-hidden flex-1 flex flex-col">
          <div className="px-3 py-2 border-b border-tint bg-tint/30 text-xs space-y-0.5 shrink-0">
            <div><span className="font-semibold text-navy">Para:</span> <span className="text-muted2">{emailForm.para || "-"}</span></div>
            <div><span className="font-semibold text-navy">Assunto:</span> <span className="text-muted2">{emailForm.assunto || "(sem assunto)"}</span></div>
          </div>
          <div
            data-testid="email-preview"
            className="p-4 bg-white overflow-y-auto email-preview-content flex-1"
            style={{ minHeight: 220, fontFamily: "-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif", color: "#0B2545", lineHeight: 1.6, fontSize: 14 }}
            dangerouslySetInnerHTML={{
              __html: emailSafeHtml(incluirAssinatura ? appendSignaturePreview(emailForm.html || "", mySignature) : (emailForm.html || "")) || "<p style=\"color:#94a3b8\">A mensagem aparecera aqui...</p>",
            }}
          />
        </div>
        {incluirAssinatura && !mySignature && (
          <p className="text-[11px] text-amber-600 mt-1 shrink-0">Ainda nao tem uma assinatura configurada - defina-a em "O meu perfil" para que apareca aqui e no email enviado.</p>
        )}
      </div>
      </div>
      <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={() => setShowEmail(false)}>Cancelar</Btn><Btn onClick={sendEmail} testid="email-send"><Send size={15} /> Enviar</Btn></div>
      </Modal>

      <Modal open={showContact} onClose={() => setShowContact(false)} title="Adicionar contacto" testid="contact-modal">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nome" value={contact.nome} onChange={(v) => setContact({ ...contact, nome: v })} testid="contact-nome" />
          <div>
            <label className="block text-xs uppercase text-muted2 mb-1">Departamento</label>
            <select
              value={contact.departamento}
              onChange={(e) => setContact({ ...contact, departamento: e.target.value })}
              data-testid="contact-departamento"
              className="w-full px-3 py-2 border border-tint rounded-lg bg-white text-sm"
            >
              <option value="">- Sem departamento -</option>
              {DEPARTAMENTOS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <Field label="Cargo (opcional)" value={contact.cargo} onChange={(v) => setContact({ ...contact, cargo: v })} testid="contact-cargo" />
          <Field label="Email" value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} testid="contact-email" />
          <Field label="Telefone" value={contact.telefone} onChange={(v) => setContact({ ...contact, telefone: v })} testid="contact-telefone" />
          <Field label="Telemovel" value={contact.telemovel} onChange={(v) => setContact({ ...contact, telemovel: v })} testid="contact-telemovel" />
        </div>
        <label className="flex items-center gap-2 text-sm text-navy cursor-pointer mt-2">
          <input type="checkbox" checked={!!contact.principal} onChange={(e) => setContact({ ...contact, principal: e.target.checked })} data-testid="contact-principal" className="rounded border-slate-300" />
          Contacto principal
        </label>
        <TextArea label="Notas" value={contact.notas} onChange={(v) => setContact({ ...contact, notas: v })} testid="contact-notas" />
        <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={() => setShowContact(false)}>Cancelar</Btn><Btn onClick={addContact} testid="contact-save">Adicionar</Btn></div>
      </Modal>

      {/* Editar contacto (a partir do card) */}
      <Modal open={!!editContact} onClose={() => setEditContact(null)} title={`Editar ${editContact?.nome || "contacto"}`} testid="contact-edit-modal" size="lg">
        {editContact && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nome" value={editContact.nome || ""} onChange={(v) => setEditContact({ ...editContact, nome: v })} testid="edit-contact-nome" />
              <div>
                <label className="block text-xs uppercase text-muted2 mb-1">Departamento</label>
                <select value={editContact.departamento || ""} onChange={(e) => setEditContact({ ...editContact, departamento: e.target.value })}
                  data-testid="edit-contact-departamento"
                  className="w-full px-3 py-2 border border-tint rounded-lg bg-white text-sm">
                  <option value="">- Sem departamento -</option>
                  {DEPARTAMENTOS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <Field label="Cargo" value={editContact.cargo || ""} onChange={(v) => setEditContact({ ...editContact, cargo: v })} testid="edit-contact-cargo" />
              <Field label="Email" value={editContact.email || ""} onChange={(v) => setEditContact({ ...editContact, email: v })} testid="edit-contact-email" />
              <Field label="Telefone" value={editContact.telefone || ""} onChange={(v) => setEditContact({ ...editContact, telefone: v })} testid="edit-contact-telefone" />
              <Field label="Telemovel" value={editContact.telemovel || ""} onChange={(v) => setEditContact({ ...editContact, telemovel: v })} testid="edit-contact-telemovel" />
            </div>
            <div className="flex items-center gap-4 mt-2">
              <label className="flex items-center gap-2 text-sm text-navy cursor-pointer">
                <input type="checkbox" checked={!!editContact.principal} onChange={(e) => setEditContact({ ...editContact, principal: e.target.checked })}
                  data-testid="edit-contact-principal" className="rounded border-slate-300" />
                Contacto principal
              </label>
              <label className="flex items-center gap-2 text-sm text-navy cursor-pointer">
                <input type="checkbox" checked={!!editContact.marketing_optin} onChange={(e) => setEditContact({ ...editContact, marketing_optin: e.target.checked })}
                  data-testid="edit-contact-optin" className="rounded border-slate-300" />
                Marketing opt-in
              </label>
            </div>
            <TextArea label="Notas" value={editContact.notas || ""} onChange={(v) => setEditContact({ ...editContact, notas: v })} testid="edit-contact-notas" />
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => setEditContact(null)}>Cancelar</Btn>
              <Btn onClick={saveEditContact} testid="edit-contact-save">Guardar</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* Drawer de visualizacao de contacto */}
      {drawerContact && (
        <ContactDrawer contact={drawerContact} open={!!drawerContact} onClose={() => setDrawerContact(null)} />
      )}

      <Modal open={showDeal} onClose={() => setShowDeal(false)} title="Nova oportunidade" testid="deal-modal">
        <Field label="Nome" value={deal.nome} onChange={(v) => setDeal({ ...deal, nome: v })} testid="deal-nome" />
        <Selecter label="Plataforma"
          value={deal.plataforma}
          onChange={(v) => setDeal({ ...deal, plataforma: v })}
          options={[{ value: "", label: "- Sem plataforma especifica -" }, ...platforms.map((p) => ({ value: p.slug || p.nome, label: p.nome || p.slug }))]}
          testid="deal-plataforma" />
        <Selecter label="Estagio" value={deal.estagio} onChange={(v) => setDeal({ ...deal, estagio: v })} options={STAGE_OPTIONS} testid="deal-estagio" />
        <Field label="MRR (EUR/mes)" type="number" value={deal.mrr} onChange={(v) => setDeal({ ...deal, mrr: v })} testid="deal-mrr" />
        {partners.length > 0 && (
          <Selecter label="Parceiro (referral)" value={deal.partner_id || ""}
            onChange={(v) => setDeal({ ...deal, partner_id: v })}
            options={[{ value: "", label: "- Sem parceiro -" }, ...partners.map((p) => ({ value: p.id, label: p.nome }))]}
            testid="deal-partner" />
        )}
        <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={() => setShowDeal(false)}>Cancelar</Btn><Btn onClick={addDeal} testid="deal-save">Criar</Btn></div>
      </Modal>

      <ActivityModal
        open={showActivity}
        onClose={() => { setShowActivity(false); setActivity({ tipo: "nota", assunto: "", corpo: "", contact_id: "", contact_ids: [], resultado: "", followup_dt: "", followup_notas: "" }); }}
        accountId={id}
        accountNome={acc.nome}
        accountNif={acc.nif}
        contacts={acc.contactos || []}
        onSuccess={loadTimeline}
        onNewContact={() => setShowContact(true)}
      />

      {/* Modal: picker de contacto para Ligar */}
      {showPhonePicker && (
        <div className="fixed inset-0 z-[1500] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="bg-navy text-white px-5 py-3 flex items-center justify-between">
              <span className="font-bold flex items-center gap-2"><Phone size={16} /> Selecionar numero</span>
              <button onClick={() => setShowPhonePicker(false)} className="hover:bg-white/10 rounded p-1"><X size={16} /></button>
            </div>
            <div className="p-4 space-y-2 max-h-72 overflow-y-auto">
              {/* Numero da conta */}
              {acc?.telefone && (
                <button
                  onClick={() => { setPhonePickerTarget(acc.telefone); setShowPhonePicker(false); }}
                  data-testid="phone-picker-account"
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-tint border border-tint transition text-left"
                >
                  <Phone size={14} className="text-navy shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-navy">{acc.nome}</p>
                    <p className="text-xs text-slate-500">{acc.telefone} - geral</p>
                  </div>
                </button>
              )}
              {/* Contactos com telefone */}
              {(acc.contactos || []).filter(c => c.telemovel || c.telefone).map((c, i) => {
                const num = c.telemovel || c.telefone;
                return (
                  <button
                    key={c.id || i}
                    onClick={() => { setPhonePickerTarget(num); setShowPhonePicker(false); }}
                    data-testid={`phone-picker-contact-${i}`}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-tint border border-tint transition text-left"
                  >
                    <Phone size={14} className="text-navy shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-navy">{c.nome}</p>
                      <p className="text-xs text-slate-500">{num}</p>
                    </div>
                  </button>
                );
              })}
              {!acc?.telefone && (acc.contactos || []).filter(c => c.telemovel || c.telefone).length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">Nenhum contacto com telefone registado.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* WebphoneDialog para Ligar apos selecionar numero */}
      <WebphoneDialog open={!!phonePickerTarget} number={phonePickerTarget} defaultExtension={myLetsCallExt} onClose={() => setPhonePickerTarget("")} />

      <SmsComposer        open={showSms}
        onClose={() => { setShowSms(false); setSmsContactos(null); }}
        contactos={smsContactos || acc.contactos || []}
        accountId={acc.id}
        nome={acc.nome}
        onSent={() => loadTimeline()}
      />

      <WhatsappComposer
        open={!!chamadasWaOpen}
        onClose={() => setChamadasWaOpen(null)}
        numero={chamadasWaOpen?.numero || ""}
        accountId={acc.id}
        nome={acc.nome}
        onSent={() => loadTimeline()}
      />

      <Modal open={!!emailPreview} onClose={() => setEmailPreview(null)} title={emailPreview?.assunto || "Email"} size="lg" testid="email-preview-modal">
        {emailPreview && (
          <div className="space-y-2">
            <div className="text-xs text-muted2">
              {(emailPreview.direcao || "enviado") === "enviado" ? `Para ${emailPreview.para || "-"}` : `De ${emailPreview.de || "-"}`}
              {" - "}{dt(emailPreview.em)}
            </div>
            <iframe title="email-preview" srcDoc={emailPreview.corpo} data-testid="email-preview-iframe"
              className="w-full h-[60vh] border border-tint rounded-lg bg-white" />
          </div>
        )}
      </Modal>

      {previewProp && (
        <ProposalDrawer
          proposal={previewProp}
          open={!!previewProp}
          onClose={() => setPreviewProp(null)}
          accounts={acc ? [{ id: acc.id, nome: acc.nome }] : []}
          onOpenPdf={async (p) => {
            try {
              const { data } = await api.get(`/cpq/proposals/${p.id}/pdf`, { responseType: "blob" });
              window.open(URL.createObjectURL(data), "_blank");
            } catch { toast.error("Nao foi possivel abrir o PDF."); }
          }}
          onOpenEditor={(p) => { setPreviewProp(null); nav(`/app/propostas?edit=${p.id}`); }}
        />
      )}

      {/* ---- Agendamentos: 4 modais partilhados pela tab -------------- */}
      <Modal open={!!meetingForm} onClose={() => setMeetingForm(null)} title="Novo agendamento" testid="meeting-form-modal">
        <Field label="Assunto" value={meetingForm?.assunto || ""} onChange={(v) => setMeetingForm({ ...meetingForm, assunto: v })} testid="meeting-assunto" />
        <div className="grid grid-cols-2 gap-2">
          <Field label="Inicio" type="datetime-local" value={meetingForm?.inicio || ""} onChange={(v) => setMeetingForm({ ...meetingForm, inicio: v })} testid="meeting-inicio" />
          <Field label="Fim" type="datetime-local" value={meetingForm?.fim || ""} onChange={(v) => setMeetingForm({ ...meetingForm, fim: v })} testid="meeting-fim" />
        </div>
        <TextArea label="Descricao" value={meetingForm?.descricao || ""} onChange={(v) => setMeetingForm({ ...meetingForm, descricao: v })} testid="meeting-descricao" />
        <label className="flex items-center gap-2 text-sm text-navy mt-1">
          <input type="checkbox" checked={!!meetingForm?.teams} onChange={(e) => setMeetingForm({ ...meetingForm, teams: e.target.checked })} data-testid="meeting-teams" />
          Gerar link Microsoft Teams
        </label>
        <label className="flex items-center gap-2 text-sm text-navy mt-1">
          <input type="checkbox" checked={!!meetingForm?.invite_client} onChange={(e) => setMeetingForm({ ...meetingForm, invite_client: e.target.checked })} data-testid="meeting-invite-client" />
          Convidar tambem o cliente
        </label>
        {meetingForm?.invite_client && (
          <div className="ml-6 mt-1">
            <Field label="Email do convidado" value={meetingForm?.client_email || ""} onChange={(v) => setMeetingForm({ ...meetingForm, client_email: v })} testid="meeting-client-email" placeholder="cliente@empresa.pt" />
            {emailContacts.length > 0 && (
              <select
                value=""
                onChange={(e) => setMeetingForm({ ...meetingForm, client_email: e.target.value })}
                data-testid="meeting-client-picker"
                className="w-full px-3 py-2 border border-tint rounded-lg bg-white text-xs text-muted2"
              >
                <option value="">... ou escolher outro contacto da ficha</option>
                {emailContacts.map((c, i) => (
                  <option key={`${c.email}-${i}`} value={c.email}>
                    {(c.nome || c.email) + (c.departamento ? ` [${c.departamento}]` : "") + (c.principal ? " *" : "") + " - " + c.email}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={() => setMeetingForm(null)}>Cancelar</Btn><Btn onClick={saveMeeting} testid="meeting-save">Criar agendamento</Btn></div>
      </Modal>

      <Modal open={!!assignMeeting} onClose={() => setAssignMeeting(null)} title="Atribuir agendamento" testid="meeting-assign-modal">
        <div>
          <label className="block text-sm font-semibold text-navy mb-1">Membro da equipa</label>
          <select value={assignMeeting?.responsavel_id || ""} onChange={(e) => setAssignMeeting({ ...assignMeeting, responsavel_id: e.target.value })} data-testid="assign-member" className="w-full px-3 py-2 border border-tint rounded-lg bg-white">
            <option value="">- Escolher -</option>
            {teamMembers.map((u) => <option key={u.id} value={u.id}>{u.nome || u.email} {u.email ? `(${u.email})` : ""}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-navy mt-3">
          <input type="checkbox" checked={!!assignMeeting?.invite_client} onChange={(e) => setAssignMeeting({ ...assignMeeting, invite_client: e.target.checked })} data-testid="assign-invite-client" />
          Convidar tambem o cliente
        </label>
        {assignMeeting?.invite_client && (
          <div className="ml-6 mt-1">
            <Field label="Email do convidado" value={assignMeeting?.client_email || ""} onChange={(v) => setAssignMeeting({ ...assignMeeting, client_email: v })} testid="assign-client-email" placeholder="cliente@empresa.pt" />
            {emailContacts.length > 0 && (
              <select
                value=""
                onChange={(e) => setAssignMeeting({ ...assignMeeting, client_email: e.target.value })}
                data-testid="assign-client-picker"
                className="w-full px-3 py-2 border border-tint rounded-lg bg-white text-xs text-muted2"
              >
                <option value="">... ou escolher outro contacto da ficha</option>
                {emailContacts.map((c, i) => (
                  <option key={`${c.email}-${i}`} value={c.email}>
                    {(c.nome || c.email) + (c.departamento ? ` [${c.departamento}]` : "") + (c.principal ? " *" : "") + " - " + c.email}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-3"><Btn variant="ghost" onClick={() => setAssignMeeting(null)}>Cancelar</Btn><Btn onClick={doAssign} testid="assign-save">Atribuir</Btn></div>
      </Modal>

      <Modal open={!!inviteMeeting} onClose={() => setInviteMeeting(null)} title="Enviar convite personalizado" testid="meeting-invite-modal">
        <Field label="Para" value={inviteMeeting?.para || ""} onChange={(v) => setInviteMeeting({ ...inviteMeeting, para: v })} placeholder="cliente@empresa.pt" testid="invite-email-para" />
        {emailContacts.length > 0 && (
          <select
            value=""
            onChange={(e) => setInviteMeeting({ ...inviteMeeting, para: e.target.value })}
            data-testid="invite-email-picker"
            className="w-full px-3 py-2 border border-tint rounded-lg bg-white text-xs text-muted2 mb-2"
          >
            <option value="">... ou escolher outro contacto da ficha (por departamento)</option>
            {emailContacts.map((c, i) => (
              <option key={`${c.email}-${i}`} value={c.email}>
                {(c.nome || c.email) + (c.departamento ? ` [${c.departamento}]` : "") + (c.principal ? " *" : "") + " - " + c.email}
              </option>
            ))}
          </select>
        )}
        <Field label="Assunto" value={inviteMeeting?.assunto || ""} onChange={(v) => setInviteMeeting({ ...inviteMeeting, assunto: v })} testid="invite-email-assunto" />
        <TextArea label="Corpo do email (editavel)" value={inviteMeeting?.corpo || ""} onChange={(v) => setInviteMeeting({ ...inviteMeeting, corpo: v })} testid="invite-email-corpo" />
        <p className="text-[11px] text-muted2">Ja inclui data/hora e link Teams. Personalize antes de enviar.</p>
        <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={() => setInviteMeeting(null)}>Cancelar</Btn><Btn onClick={sendInvite} testid="invite-email-send"><Send size={14} /> Enviar convite</Btn></div>
      </Modal>

      <Modal open={!!reschedMeeting} onClose={() => setReschedMeeting(null)} title="Reagendar" testid="meeting-reschedule-modal">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Novo inicio" type="datetime-local" value={reschedMeeting?.inicio || ""} onChange={(v) => setReschedMeeting({ ...reschedMeeting, inicio: v })} testid="resched-inicio" />
          <Field label="Novo fim" type="datetime-local" value={reschedMeeting?.fim || ""} onChange={(v) => setReschedMeeting({ ...reschedMeeting, fim: v })} testid="resched-fim" />
        </div>
        <p className="text-[11px] text-muted2">O convite Teams sera actualizado automaticamente para os participantes ja convidados.</p>
        <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={() => setReschedMeeting(null)}>Cancelar</Btn><Btn onClick={saveReschedule} testid="resched-save">Guardar</Btn></div>
      </Modal>
    </>
  );
}

function SectionBox({ title, children, action }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="text-navy font-semibold text-[13px] tracking-tight uppercase text-slate-500">{title}</h3>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      {children}
    </div>
  );
}
function FGrid({ children }) { return <div className="grid grid-cols-2 md:grid-cols-3 gap-3">{children}</div>; }
function F({ label, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-[13px] text-navy mt-0.5 break-words">{v == null || v === "" ? "-" : v}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Timing Widget - mostra o BANT-timing da conta e dias restantes
// ---------------------------------------------------------------------------
const TIMING_END_DAYS = { "0-30d": 30, "1-3m": 90, "3-6m": 180, "6-12m": 365, "> 12m": 540 };

function TimingWidget({ acc, onAction }) {
  const timing = acc.bant_timing;
  const ref = acc.bant_timing_definido_em || acc.atualizado_em || acc.criado_em;
  const total = TIMING_END_DAYS[timing];
  let diasAteFim = null;
  if (ref && total != null) {
    const start = new Date(ref).getTime();
    const now = Date.now();
    const dias = Math.floor((now - start) / (1000 * 60 * 60 * 24));
    diasAteFim = Math.max(0, total - dias);
  }
  const urgent = diasAteFim != null && diasAteFim <= 14;
  const warn = diasAteFim != null && diasAteFim <= 45 && !urgent;
  const tone = urgent
    ? { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", accent: "text-red-600" }
    : warn
    ? { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", accent: "text-amber-600" }
    : { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-700", accent: "text-sky-600" };

  const labels = {
    "0-30d": "Fecho em <30 dias",
    "1-3m": "Fecho em 1-3 meses",
    "3-6m": "Fecho em 3-6 meses",
    "6-12m": "Fecho em 6-12 meses",
    "> 12m": "Fecho > 12 meses",
    "indefinido": "Timing por definir",
  };

  const contactar = () => {
    if (onAction) onAction();
    // Scroll to activities section to log a contact
    setTimeout(() => {
      const el = document.querySelector('[data-testid="account-actions-nova-atividade"]');
      if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 200);
  };

  return (
    <div className={`mb-4 border rounded-lg px-4 py-2.5 flex items-center gap-3 ${tone.bg} ${tone.border}`} data-testid="timing-widget">
      <Target size={16} className={`${tone.accent} shrink-0`} />
      <div className="min-w-0 flex-1">
        <div className={`text-[10px] uppercase tracking-wider font-semibold ${tone.text}`}>Timing de fecho (BANT)</div>
        <div className="text-sm">
          <span className={`font-bold ${tone.text}`}>{labels[timing] || timing}</span>
          {diasAteFim != null && (
            <span className={`ml-2 text-[11px] font-mono ${tone.accent}`} data-testid="timing-widget-dias">
              {diasAteFim === 0 ? "janela terminou" : `restam ${diasAteFim}d`}
            </span>
          )}
          {urgent && (
            <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-600 text-white uppercase">Urgente</span>
          )}
        </div>
      </div>
      <button onClick={contactar} data-testid="timing-widget-cta"
        className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-white text-xs font-semibold ${urgent ? "bg-red-600 hover:bg-red-700" : warn ? "bg-amber-600 hover:bg-amber-700" : "bg-blue hover:bg-[var(--blue-hover)]"}`}>
        <Phone size={12} /> Contactar agora
      </button>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Tab Marketing na ficha da conta (Iter107)
// ---------------------------------------------------------------------------
function AccountMarketingTab({ accountId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState({});
  const nav = useNavigate();

  const load = () => {
    if (!accountId) return;
    api.get(`/crm/accounts/${accountId}/marketing-lists`)
      .then((r) => setData(r.data))
      .catch(() => setData({ lists: [], sem_email: [], contacts_total: 0 }));
  };
  useEffect(load, [accountId]);

  const bulk = async (listId, action) => {
    setBusy((b) => ({ ...b, [listId]: action }));
    try {
      const { data: r } = await api.put(`/crm/accounts/${accountId}/marketing-lists/bulk`, {
        list_id: listId, action,
      });
      toast.success(`${action === "subscribe" ? "Subscritos" : "Dessubscritos"}: ${r.processed}${r.skipped_no_email ? ` (${r.skipped_no_email} sem email)` : ""}`);
      load();
    } catch (e) {
      toast.error("Nao foi possivel actualizar.");
    } finally {
      setBusy((b) => { const c = { ...b }; delete c[listId]; return c; });
    }
  };

  if (!data) return <div className="text-sm text-muted2">A carregar subscricoes...</div>;

  return (
    <div className="space-y-4" data-testid="account-marketing-tab">
      <div className="border border-tint rounded-xl bg-white p-4">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div>
            <div className="text-sm font-semibold text-navy">Subscricoes agregadas</div>
            <div className="text-[11px] text-muted2">
              <b className="text-navy">{data.contacts_total}</b> contactos na conta - <b>{data.contacts_com_email}</b> com email - <b className="text-emerald-600">{data.contacts_subscritos}</b> subscritos a listas
            </div>
          </div>
          <Btn variant="ghost" onClick={() => nav("/app/marketing")} testid="acc-marketing-open">
            <ExternalLink size={14} /> Marketing
          </Btn>
        </div>
        {data.sem_email.length > 0 && (
          <div className="text-[11px] text-amber-600 mb-2">
            <b>{data.sem_email.length}</b> contactos sem email nao entram em listas: {data.sem_email.slice(0, 3).map((c) => c.nome).join(", ")}{data.sem_email.length > 3 ? "..." : ""}
          </div>
        )}
      </div>

      {(data.lists || []).length === 0 && (
        <div className="text-sm text-muted2 border border-dashed border-tint rounded-lg p-4 text-center">
          Sem listas de marketing criadas. Crie em /app/marketing.
        </div>
      )}
      {(data.lists || []).map((l) => {
        const busyAction = busy[l.id];
        return (
          <div key={l.id} data-testid={`acc-list-${l.id}`} className="border border-tint rounded-xl bg-white overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-b border-tint bg-slate-50">
              <div className="flex items-center gap-2">
                <Users size={14} className="text-blue" />
                <div>
                  <div className="text-sm font-semibold text-navy">{l.nome}</div>
                  <div className="text-[11px] text-muted2">
                    {l.plataforma ? `${l.plataforma} - ` : ""}
                    <b className={l.total_subscritos > 0 ? "text-emerald-600" : "text-slate-400"}>{l.total_subscritos}</b> de <b>{data.contacts_com_email}</b> contactos subscritos
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => bulk(l.id, "subscribe")} disabled={busyAction === "subscribe" || l.total_subscritos === data.contacts_com_email}
                  data-testid={`acc-bulk-sub-${l.id}`}
                  className={`text-[11px] font-semibold px-2 py-1 rounded border ${l.total_subscritos === data.contacts_com_email ? "border-tint text-slate-300 cursor-not-allowed" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}>
                  {busyAction === "subscribe" ? "A subscrever..." : "Subscrever todos"}
                </button>
                <button onClick={() => bulk(l.id, "unsubscribe")} disabled={busyAction === "unsubscribe" || l.total_subscritos === 0}
                  data-testid={`acc-bulk-unsub-${l.id}`}
                  className={`text-[11px] font-semibold px-2 py-1 rounded border ${l.total_subscritos === 0 ? "border-tint text-slate-300 cursor-not-allowed" : "border-red-200 text-red-700 hover:bg-red-50"}`}>
                  {busyAction === "unsubscribe" ? "A remover..." : "Remover todos"}
                </button>
              </div>
            </div>
            {l.subscritos.length > 0 && (
              <div className="divide-y divide-tint">
                {l.subscritos.map((s, i) => (
                  <div key={s.contact_id + i} className="px-4 py-2 flex items-center gap-3 text-[12px]">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-navy truncate">{s.nome || s.email}</div>
                      <div className="text-[10px] text-slate-500 truncate">{s.email}</div>
                    </div>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${s.estado === "unsubscribed" ? "bg-red-100 text-red-700" : s.estado === "bounced" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{s.estado}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
